// The 3D look: an orthographic Three.js scene that sits under the PixiJS canvas.
// The camera is the same dimetric view the 2D art was drawn in (30 degrees up,
// 45 degrees round), so the ground, buildings and the 2D units, labels and effects
// that stay on Pixi all land on the same tiles. The Pixi world's pan and zoom are
// copied across every frame, and the view angle comes from iso.ts.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { FXAAPass } from 'three/addons/postprocessing/FXAAPass.js';
import type { Application, Container } from 'pixi.js';
import { DEFAULT_CONFIG, MAP_SIZE, STANDOFF, TICKS_PER_SECOND, TILE, roadsOf, type MissileArc, type Snapshot, type UnitSnapshot, PLACE_MARGIN } from '@thrash/sim';
import { toTile, viewAngle, TILE_W } from './iso.ts';
import { GpuTimer } from './perf.ts';
import { COLORS, DEATH_MS, JETS, LOW_LAUNCHERS, MISSILE_LAUNCHERS, shotLife, unit3DStyle } from './render.ts';
import { buildingFx3D, buildings3D, buildingsVersion, claim3D, deaths3D, incoming3D, road3D, shots3D, smoke3D, units3D } from './view3d.ts';
import { FLOOR_PAD, floorMaterial, setFloorRoad } from './floor3d.ts';
import type { DeathFx, DrawableBuilding, LiveShot } from './render.ts';
import { SEA_OUT, Sea3D } from './sea3d.ts';
import { Ragdolls } from './ragdoll3d.ts';
import { Debris3D } from './debris3d.ts';
import { Outskirts3D, terrain } from './outskirts3d.ts';
import { stripSlab } from './bases3d.ts';
import { grassTuft } from './ground3d.ts';
import { UnitMotion, cloneModel, mergeSkinnedParts } from './unitmotion.ts';
import { CombatFx3D, type Calibre } from './combatfx3d.ts';
import { Crowd, type CrowdMember } from './crowd3d.ts';

/** How long a fallen soldier lies before sinking away, and how long the sinking takes, ms. */
const CORPSE_MS = 3500;
const SINK_MS = 1500;
/** Sim units to tiles. */
const sh2t = (v: number) => v / TILE;
/** How far the countryside runs past the map edge, in tiles, and how many tiles one texture repeat covers. */
const COUNTRY_MARGIN = 200;
const COUNTRY_REPEAT = 8;
const SKIN = 0xd9b08c;
const WRECK = 0x2a2a2a;
const RUBBLE = 0x3b3a35;
const FLAG_COLOR = 0xd14b4b;
const EMP_BLUE = 0x6fe3ff;
/** Behind the scene, where the countryside runs out. */
const CLEAR = 0x10140e;

// ---- look ----------------------------------------------------------------------------
// Lit for an RTS read: a sun that casts shadows, a soft studio environment so painted and
// metal surfaces catch light, neutral tone mapping (keeps the palette, rolls off highlights),
// bloom on anything brighter than white (tracers, flashes, blasts), and a rim of team colour
// round every unit's silhouette. There is one look, drawn the same on every machine: what it
// costs is kept down by how things are drawn (crowd3d.ts, the instanced tufts, trees and
// debris), not by a quality setting. Screen-space ambient occlusion was tried and dropped: it
// drew the scene again for its normals and was a third of the GPU's frame. ?fx=0 draws
// without the post-processing.
const POST = new URLSearchParams(location.search).get('fx') !== '0';
/**
 * Device pixels per CSS pixel the scene is drawn at, at most. A high-density screen is capped:
 * the post-processing works every pixel several times over, and at the full two the chain was
 * most of the frame; FXAA at the end smooths what the lower ratio leaves.
 */
const PIXEL_RATIO_CAP = 1.5;
const SUN_DIR = new THREE.Vector3(-30, 60, 20).normalize();
const SHADOW_MAP = 2048;
/** HDR boost for things that glow, so they clear the bloom threshold. */
const GLOW = 4;
/** Team colours: the rim round each unit. Buildings get a faint warm rim. */
const TEAM = { defender: 0x3d9bff, attacker: 0xff4630 } as const;
const BUILDING_RIM = 0xffe6c0;
/** How long a destroyed building's rubble heap takes to rise out of the blast. */
const HEAP_MS = 900;
/**
 * How much each unit model is scaled up. Sizes keep real-world proportions against a
 * soldier about 0.4 tiles tall (stretched a little where life would be too big or too small
 * to read): a tank is about four soldiers long and as tall as one, a helicopter a bit bigger,
 * a destroyer dwarfs a patrol boat. The comment is the drawn size in tiles.
 */
const UNIT_SCALE: Record<string, number> = {
  // a tower defence reads from high up: the men and the small drones are drawn half again as big as life
  rifleman: 1.0, marksman: 1.0, conscript: 1.0, at_team: 1.0, soldier: 1.0, // 0.6 tall
  heavy_gunner: 0.92,  // 0.7 tall, bulkier
  tank: 1.4,           // 1.3 hull, 1.7 with the gun, 0.5 tall
  attack_heli: 1.6,    // 2.1 long, 1.6 across the rotor
  scout_drone: 0.9,    // 0.55 across: a quadcopter
  fpv_drone: 1.2,      // 0.36 across: a racing quad
  attack_drone: 2.0,   // 0.8 wingspan: a loitering munition
  shahed: 2.0,         // 1.2 wingspan
  cruise_missile: 2.2, // 1.4 long
  kalibr: 2.2, emp_missile: 2.2,
  ballistic: 2.2,      // 1.6 long, fatter
  hgv: 2.2, hgv_nuclear: 2.2, // 1.5 long, a flat wedge
  icbm: 2.2,           // 2.2 long
  submarine: 2.0,      // the Borei, awash
  nuke: 2.0,           // 0.9 long: the re-entry vehicle
  boat: 1.6,           // 1.6 long
  landing_craft: 1.6,  // 2.1 long, 1.1 wide
  destroyer: 2.0,      // 4.4 long
  drone_ship: 2.0,     // a container ship: the hovercraft's hull, drawn as big as the frigate
};
/** Fallback for a model with no entry above. */
const KIND_SCALE: Record<string, number> = { foot: 1.0, vehicle: 1.6, ship: 1.6, air: 1.8, drone: 1.5, missile: 2.2 };

/**
 * A copy of a material with a fresnel rim added to its emissive light: upward-facing surfaces
 * turning away from the camera glow in the given colour, which outlines the top of the silhouette.
 */
function withRim<T extends THREE.Material>(base: T, color: number, strength: number): T {
  const m = base.clone();
  const rim = { uRimColor: { value: new THREE.Color(color) }, uRimStrength: { value: strength } };
  m.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, rim);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uRimColor;\nuniform float uRimStrength;')
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        {
          vec3 rimView = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);
          float rim = pow(1.0 - clamp(dot(normal, rimView), 0.0, 1.0), 4.0);
          // only on surfaces facing up: undersides and lower flanks never glow
          rim *= smoothstep(0.05, 0.6, inverseTransformDirection(normal, viewMatrix).y);
          totalEmissiveRadiance += uRimColor * rim * uRimStrength;
        }`);
  };
  m.customProgramCacheKey = () => 'rim';
  m.userData.shared = true;
  return m;
}

/** A thin soft ring over a faint fill, white, tinted by the material colour. */
function glowTexture(): THREE.CanvasTexture {
  const n = 64;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
  grad.addColorStop(0, 'rgba(255,255,255,0.12)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.12)');
  grad.addColorStop(0.78, 'rgba(255,255,255,0.9)');
  grad.addColorStop(0.92, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, n, n);
  return new THREE.CanvasTexture(c);
}

/** A stable 0..1 number for a seed and a slot, so each building's smoke always rises from the same places. */
const rnd = (seed: number, k: number) => {
  const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453;
  return v - Math.floor(v);
};

type Axis = 'x' | 'y' | 'z';

/** The parts of one building that move or change: smoke, EMP ring, spinning parts, HQ flag. */
interface BuildingFx {
  puffs: THREE.Mesh[];
  ring?: THREE.Mesh;
  /** C-RAM barrels (spin up while firing) or turbine blades (turn steadily), and the axis they turn on. */
  spin?: { node: THREE.Object3D; axis: Axis; base: number; angle?: number; rate?: number; last?: number };
  flag?: THREE.Mesh;
  /** A turret head that swings onto its target, and where it sits when it is not kicking back. */
  head?: { node: THREE.Object3D; base: THREE.Vector3 };
  /** The missile defence's radar dish, turning. */
  radar?: THREE.Object3D;
  /** The Command Center's beacons: a steady blink, an erratic flicker once it is badly hurt. */
  beacon?: { node: THREE.Object3D; hurt: boolean };
  /** The sniper nest's marksman, who turns to face his shots, kicks back on fire, and scans the field between them. */
  sniper?: { node: THREE.Object3D; home: THREE.Vector3; base: number; aim: number; kick: number; shot: LiveShot | null; firedAt: number; motion: UnitMotion; last: number };
  /** Height of the roof, where smoke rises from. */
  top: number;
  seed: number;
}

/**
 * Frees a subtree's geometry and materials, except those shared with other meshes (colours,
 * loaded models), and the bone textures of its skeletons: every skinned copy has its own, and
 * left behind they were a texture on the GPU for every soldier ever killed.
 */
function disposeTree(o: THREE.Object3D): void {
  const skeletons = new Set<THREE.Skeleton>();
  o.traverse(c => {
    if (c instanceof THREE.Mesh) {
      if (!c.geometry.userData.shared) c.geometry.dispose();
      for (const m of [c.material as THREE.Material | THREE.Material[]].flat()) if (!m.userData.shared) m.dispose();
    }
    if ((c as THREE.SkinnedMesh).isSkinnedMesh) skeletons.add((c as THREE.SkinnedMesh).skeleton);
  });
  for (const s of skeletons) s.dispose();
}

// ---- models --------------------------------------------------------------------------
// One .glb per unit and building in client/public/models, exported from the Blender
// catalog by scripts/export-models.py: centred on the origin, base on the ground, 1 unit
// to a tile, units facing +X. Until a model has arrived (or if it fails) the simple
// shapes below stand in for it.

/** Heaps of rubble for destroyed buildings, by the footprint they were made for. */
const RUBBLE_MODELS: [number, string][] = [[2, 'rubble_small'], [3, 'rubble_medium'], [4, 'rubble_large']];

/**
 * Buildings drawn with their top cut away at this height, so the camera can see who is
 * inside: the sniper nest loses its roof and shows its marksman.
 */
const CUTAWAY: Record<string, number> = { sniper_nest: 1.34 };

/** The marksman in the sniper nest: the middle of the cabin floor he stands on, in the model's own frame (the stairs push the cabin off-centre), and how much the infantry model is scaled up to read from above. */
const SNIPER_SPOT = new THREE.Vector3(-0.24, 0.97, 0);
const SNIPER_SCALE = 1.05;
/** How long the marksman keeps aiming where he last fired before he goes back to scanning, in ms. */
const SNIPER_HOLD = 2500;

/** Parts that turn, by node name, and whether they turn on the part's shortest side (blades) or its longest (barrels). */
const SPINNERS: Record<string, 'thin' | 'long'> = { blades: 'thin', barrels: 'long' };

/** The C-RAM's barrel spin while firing, in radians a millisecond: 750 turns a minute. */
const CRAM_SPIN = (750 * Math.PI * 2) / 60000;

/**
 * Turret heads that swing onto their targets: which way the gun points when the head is unturned
 * (radians, in the model's own frame), how fast it slews (radians a second), how far the
 * muzzle sits out from the pivot and up from it, how hard it kicks back, and its calibre.
 */
const TURRETS: Record<string, { rest: number; slew: number; reach: number; up: number; kick: number; calibre: Calibre }> = {
  mg_nest: { rest: Math.PI / 4, slew: 7, reach: 0.5, up: 0.2, kick: 0.055, calibre: 'heavy' },
  howitzer: { rest: 0, slew: 2.2, reach: 1.28, up: 0.98, kick: 0.14, calibre: 'artillery' },
  air_defence: { rest: -Math.PI / 2, slew: 4.5, reach: 0.42, up: 0.63, kick: 0.065, calibre: 'heavy' },
  missile_battery: { rest: 0, slew: 2.5, reach: 0.5, up: 0.55, kick: 0.06, calibre: 'heavy' },
  drone_interceptor: { rest: 0, slew: 8.5, reach: 0.62, up: 0.2, kick: 0.045, calibre: 'heavy' },
  c_ram: { rest: 0, slew: 9.5, reach: 0.85, up: 0.55, kick: 0.025, calibre: 'small' },
};
/** What each unit's gun is, for the size of its flash, tracer and hit. */
const UNIT_CALIBRE: Record<string, Calibre> = {
  marksman: 'sniper', heavy_gunner: 'heavy', tank: 'cannon', attack_heli: 'heavy', boat: 'heavy', destroyer: 'cannon', landing_craft: 'heavy',
  strike_jet: 'cannon', f16: 'heavy',
};
/** Where a unit's muzzle sits: how far out in front and how high off its base, in tiles. */
const MUZZLE: Record<string, [number, number]> = {
  foot: [0.24, 0.27], vehicle: [0.95, 0.33], ship: [0.5, 0.3], air: [0.75, -0.12], drone: [0.2, 0], missile: [0.3, 0],
};

/** The axis along which a node's own geometry is thinnest or longest. */
function spinAxis(node: THREE.Object3D, which: 'thin' | 'long'): Axis {
  const box = new THREE.Box3();
  const meshes = node instanceof THREE.Mesh ? [node] : node.children.filter((c): c is THREE.Mesh => c instanceof THREE.Mesh);
  for (const m of meshes) {
    m.geometry.computeBoundingBox();
    const b = m.geometry.boundingBox!.clone();
    if (m !== node) b.applyMatrix4(m.matrix);
    box.union(b);
  }
  const s = box.getSize(new THREE.Vector3());
  const axes: Axis[] = ['x', 'y', 'z'];
  return axes.reduce((best, a) => ((which === 'thin' ? s[a] < s[best] : s[a] > s[best]) ? a : best));
}

/**
 * A soldier who has just been killed: his death clip plays while his body is thrown by whatever hit
 * him (hard by a blast, a stagger by a bullet), tumbles, lands and settles flat, then sinks away.
 */
interface Ragdoll {
  g: THREE.Group;
  /** A soldier's animation (his death clip plays as he flies); a machine has none. */
  motion?: UnitMotion;
  /** A machine thrown by a nuke: it blows apart where it lands. */
  machine?: { type: string; color: THREE.Color; size: number; burst: boolean };
  at: number;
  vel: THREE.Vector3;
  /** The world axis the body tumbles about, how far it has turned, how fast, and the pose it died in. */
  axis: THREE.Vector3;
  ang: number;
  rate: number;
  base: THREE.Quaternion;
  landed: boolean;
}

/** Shots that throw a man when they kill him: shells, rockets, mines, charges and blasts. */
const EXPLOSIVE = new Set(['tank', 'destroyer', 'at_team', 'attack_drone', 'fpv_drone', 'shahed', 'strike_jet', 'cruise_missile', 'kalibr', 'land_mine', 'at_mine', 'sea_mine',
  'charge', 'nuke', 'ballistic', 'hgv', 'hgv_nuclear', 'icbm', 'missile_battery', 'bastion', 'atgm_post', 'howitzer', 'attack_heli', 'boat']);

/** Real explosions on the ground, and how far (tiles) and how hard they throw loose debris: not gunfire. */
const KICKS: Record<string, [number, number]> = {
  tank: [1.2, 3.5], destroyer: [1.2, 3.5], at_team: [1, 3], attack_drone: [1.6, 4.5], fpv_drone: [1, 3], shahed: [2.2, 6],
  strike_jet: [2.4, 7], cruise_missile: [2, 5.5], kalibr: [2, 5.5], land_mine: [1.4, 5], at_mine: [1.6, 6], atgm_post: [1, 3], howitzer: [1.8, 5.5], bastion: [1.6, 5], missile_battery: [1.2, 4],
};

/** Defence missiles in flight, drawn like units, with ids far above any the sim gives a unit in a game. */
const PROJECTILE_ID_BASE = 1_000_000_000;
type Flying = UnitSnapshot & { targetId?: number; coasting?: boolean };

function inFlight(s: Snapshot): Flying[] {
  return (s.projectiles ?? []).map(p => ({
    id: PROJECTILE_ID_BASE + p.id, type: 'interceptor', x: p.x, y: p.y, hp: 1, maxHp: 1, disabled: false, defender: true,
    arc: p.arc, targetId: p.targetId, ...(p.coasting ? { coasting: true } : {}),
  }));
}

/**
 * Height of a missile on its arc, in tiles: it leaves the launcher low, climbs high over the middle of
 * its flight and comes down onto its target, ending at `endY`. Longer flights climb higher.
 */
function arcLift(a: MissileArc, x: number, y: number, endY: number, height = 1): number {
  const d0 = Math.hypot(x - a.fromX / TILE, y - a.fromY / TILE);
  const d1 = Math.hypot(a.toX / TILE - x, a.toY / TILE - y);
  const total = d0 + d1;
  if (total < 1e-3) return endY;
  const t = d0 / total;
  // a ballistic missile (height above 1) climbs far higher: it leaves the water near vertical
  const peak = Math.min(10 * height, (1.5 + total * 0.45) * height);
  return 0.6 + (endY - 0.6) * t + peak * 4 * t * (1 - t);
}

/** Systems drawn with another's model until they get their own. */
const MODEL_STAND_IN: Record<string, string> = {
  patriot: 'missile_defence', thaad: 'missile_defence',
  bastion: 'missile_battery', atgm_post: 'sniper_nest', at_mine: 'land_mine', strike_jet: 'jet', airbase: 'airfield', interceptor: 'kalibr', drone_ship: 'landing_craft', hgv_nuclear: 'hgv',
};

/** Loads each model once, on first ask. */
class Models {
  private readonly loader = new GLTFLoader();
  private readonly done = new Map<string, THREE.Object3D | null>();
  private readonly pending = new Set<string>();
  /** Goes up each time a model arrives, so the scene knows to swap it in. */
  version = 0;

  /** The loaded model to clone, or null while it is loading or if it failed. */
  get(type: string): THREE.Object3D | null {
    // systems without a model of their own yet borrow the nearest look
    const name = MODEL_STAND_IN[type] ?? type;
    const m = this.done.get(name);
    if (m !== undefined) return m;
    if (this.pending.has(name)) return null;
    this.pending.add(name);
    this.loader.load(`${import.meta.env.BASE_URL}models/${name}.glb`, gltf => {
      const root = gltf.scene;
      // the infantry's clips (walk, run, shoot, die), played by unitmotion.ts
      root.animations = gltf.animations;
      root.traverse(o => {
        if (o instanceof THREE.Mesh) {
          // clones share these, so a removed clone must not free them
          o.geometry.userData.shared = true;
          o.castShadow = o.receiveShadow = true;
          for (const mat of [o.material as THREE.Material | THREE.Material[]].flat()) {
            mat.userData.shared = true;
            // a see-through two-sided material (the chain-link fences) is otherwise drawn twice, back
            // then front, and switching sides between the two recompiles its shader check every draw
            if (mat.transparent && mat.side === THREE.DoubleSide) mat.forceSinglePass = true;
          }
        }
        const spin = SPINNERS[o.name];
        if (spin) o.userData.axis = spinAxis(o, spin);
      });
      // the square slab most buildings stand on goes, and a patch of worn ground takes its place
      stripSlab(name, root);
      mergeSkinnedParts(root);
      const bounds = new THREE.Box3().setFromObject(root);
      root.userData.height = bounds.max.y;
      // buildings stand on flat ground at y = 0, so a world-space plane cuts every copy at the same height
      const cut = CUTAWAY[name];
      if (cut !== undefined) {
        const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), cut);
        root.traverse(o => {
          if (o instanceof THREE.Mesh) for (const mat of [o.material as THREE.Material | THREE.Material[]].flat()) mat.clippingPlanes = [plane];
        });
        root.userData.height = Math.min(bounds.max.y, cut);
      }
      root.userData.footprint = Math.max(bounds.max.x - bounds.min.x, bounds.max.z - bounds.min.z);
      this.done.set(name, root);
      this.version++;
    }, undefined, err => {
      console.warn(`3D model ${name} did not load; drawing the simple shape instead`, err);
      this.done.set(name, null);
    });
    return null;
  }
}

const box = (w: number, h: number, d: number) => new THREE.BoxGeometry(w, h, d);
const MISSILE_TYPES = new Set([...MISSILE_LAUNCHERS, 'ship_intercept']);
/** How far a nuke's and an EMP's blast reaches, in tiles, as the sim has it, so the marks and the blast match the damage. */
const STRIKE_RADIUS = {
  nuke: (DEFAULT_CONFIG.units.nuke?.strike?.radius ?? TILE * 3) / TILE,
  emp: (DEFAULT_CONFIG.units.emp?.strike?.radius ?? TILE * 3) / TILE,
  artillery: (DEFAULT_CONFIG.abilities.artillery.radius ?? TILE * 3) / TILE,
};

// Screen pixels per tile along the ground, at zoom 1: the 2D art draws a tile as a
// TILE_W-wide diamond, and a 30 degree ortho view of a unit tile is sqrt(2) times that.
const PX_PER_UNIT = TILE_W / 2 * Math.SQRT2;
const C = MAP_SIZE / 2;
const ELEVATION = Math.PI / 6;

/** A tile-space point (unrotated, as iso.ts uses) on the ground, in scene units. */
function ground(tx: number, ty: number, h = 0): [number, number, number] {
  const a = viewAngle();
  const cos = Math.cos(a), sin = Math.sin(a);
  const dx = tx - C, dy = ty - C;
  return [dx * cos - dy * sin, h, dx * sin + dy * cos];
}

export class Scene3D {
  private readonly renderer: THREE.WebGLRenderer;
  /** The canvas to place under the Pixi one. */
  get canvas(): HTMLCanvasElement {
    return this.renderer.domElement;
  }
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private readonly groundMesh: THREE.Mesh;
  /** Grass clumps and pebbles strewn over the village ground, turned with the view; hidden under buildings. */
  private readonly tufts = new THREE.Group();
  private readonly countryside: THREE.Mesh;
  /** The far side of the coastal channel. */
  private readonly farBank: THREE.Mesh;
  private readonly sea: Sea3D;
  private readonly outskirts = new Outskirts3D();
  private readonly buildingGroup = new THREE.Group();
  private readonly models = new Models();
  private builtVersion = -1;
  private builtModels = -1;
  private builtAngle = NaN;
  private readonly unitGroup = new THREE.Group();
  /** The infantry: every soldier of a type one instance of one mesh, his clips played in the shader. */
  private readonly crowd = new Crowd();
  private readonly units = new Map<number, THREE.Group>();
  /** Each unit's animation, by unit id. */
  private readonly motions = new Map<number, UnitMotion>();
  /** Soldiers playing out their death after the raid has dropped them. */
  private dying: Ragdoll[] = [];
  /** The latest nuke: where it went off, when, and its blast radius in tiles, so what it brings down is thrown from it. */
  private lastNuke: { at: THREE.Vector3; when: number; r: number } | null = null;
  /** The last place each missile, drone and aircraft was drawn, by unit id. */
  private readonly lastInAir = new Map<number, THREE.Vector3>();
  private lastUnitFrame = 0;
  private readonly materials = new Map<number, THREE.MeshLambertMaterial>();
  private readonly marks: THREE.Mesh[] = [];
  private seenSmoke = new WeakSet<object>();
  /** A white sheet over the view, for a nuke's flash. */
  private whiteSheet: HTMLDivElement | null = null;
  private fxByBuilding = new Map<string, BuildingFx>();
  private readonly sun: THREE.DirectionalLight;
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  /** The pixel ratio the composer's targets were sized for; the screen's can change under a window moved between displays. */
  private composerRatio = 0;
  /** Whether the dressing that can wait for the first frame (tufts, the post-processing) is in. */
  private finished = false;
  /** The village's unclaimed land, shaded, with the claim outlined; rebuilt when the claim or the view angle changes. */
  private claimGroup: THREE.Group | null = null;
  private claimKey = '';
  /** Rim-lit copies of the loaded models' materials, by material and colour. */
  private readonly rimmed = new Map<string, THREE.Material>();
  private readonly glow = glowTexture();
  private readonly debris = new Debris3D();
  /** The bodies of the fallen: jointed ragdolls that take over from a soldier's model when he dies. */
  private readonly bodies = new Ragdolls();
  private readonly fx = new CombatFx3D();
  /** Shots and deaths already set off, so each bangs once. */
  private seenShots = new WeakSet<LiveShot>();
  private seenDeaths = new WeakSet<object>();
  /** Where a shot left its gun, when the shooter knows better than the sim (a turret's barrel, a tank's muzzle). */
  private muzzles = new WeakMap<LiveShot, THREE.Vector3>();
  /** Each turret's aim, kept by building across rebuilds of the building meshes. */
  private readonly turrets = new Map<string, { yaw: number; scan: number; kick: number; firedAt: number; last: number; shot: LiveShot | null }>();
  /** Whether each building (by "x,y") was destroyed when last built, to catch the moment it goes. */
  private readonly wasDead = new Map<string, boolean>();
  /** When each building fell, so its rubble heap can rise out of the blast. */
  private readonly diedAt = new Map<string, number>();
  /** Rubble heaps still growing: the model, its full scale and the building it belongs to. */
  private heaps: { obj: THREE.Object3D; full: number; key: string }[] = [];
  /** Each building's meshes as last built (by "x,y"), with what they showed, so an unchanged one is kept. */
  private built = new Map<string, { sig: string; objs: THREE.Object3D[]; fx: BuildingFx | null }>();
  /** The footprints the grass was last cleared for. */
  private tuftsFor = '';
  /** The map whose road the floor is drawing. */
  private roadKey = '';
  /** Translucent tinted materials for a tower being placed, by colour. */
  private readonly ghostMats = new Map<number, THREE.MeshLambertMaterial>();
  /** Times the scene's GPU work for the readout in the title bar. */
  readonly gpuTimer: GpuTimer;

  constructor() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(this.pixelRatio());
    this.renderer.setClearColor(CLEAR, 1);
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.shadowMap.enabled = true;
    this.renderer.localClippingEnabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.gpuTimer = new GpuTimer(this.renderer.getContext() as WebGL2RenderingContext);
    this.renderer.domElement.style.position = 'fixed';
    this.renderer.domElement.style.inset = '0';
    this.renderer.domElement.style.zIndex = '0';
    this.renderer.domElement.style.pointerEvents = 'none';

    // the camera sits 500 back (see sync); a tight depth range gives the AO pass more depth precision
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 100, 1000);
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.45;
    pmrem.dispose();
    this.scene.add(new THREE.HemisphereLight(0xcfdcff, 0x3a3323, 0.9));
    const sun = this.sun = new THREE.DirectionalLight(0xfff0d8, 2.8);
    sun.position.copy(SUN_DIR).multiplyScalar(100);
    sun.castShadow = true;
    sun.shadow.mapSize.set(SHADOW_MAP, SHADOW_MAP);
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = 0.02;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 300;
    this.scene.add(sun, sun.target);
    this.scene.add(this.buildingGroup);
    this.scene.add(this.debris.group);
    this.scene.add(this.bodies.group);
    this.bodies.groundAt = (X, Z) => {
      // back from the scene's turned frame to tiles, and the outskirts' height there
      const a = viewAngle(), cos = Math.cos(a), sin = Math.sin(a);
      return terrain(X * cos + Z * sin + C, -X * sin + Z * cos + C);
    };
    // a limb torn off pumps blood from the joint for a moment
    this.bodies.onBleed = (at, dir) => this.fx.bloodSpurt(at, dir);
    this.unitGroup.add(this.crowd.group);
    this.scene.add(this.unitGroup);

    this.scene.add(this.fx.group, this.fx.lightGroup);

    // the floor: meadow, bare earth and the road, all from one shader (floor3d.ts)
    this.groundMesh = new THREE.Mesh(new THREE.BufferGeometry(), floorMaterial());
    this.groundMesh.receiveShadow = true;
    this.scene.add(this.groundMesh);
    this.scene.add(this.tufts);

    // the countryside beyond the map: a wider, rougher patch of ground, as the 2D look had
    this.countryside = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshLambertMaterial({ map: countrysideTexture(), side: THREE.DoubleSide }));
    // well below the outskirts, whose hollows dip a little under the base's level
    this.countryside.position.y = -1;
    this.countryside.receiveShadow = true;
    this.scene.add(this.countryside);
    this.farBank = new THREE.Mesh(new THREE.BufferGeometry(), this.countryside.material);
    this.farBank.receiveShadow = true;
    this.scene.add(this.farBank);

    // the sea over the west edge of the map and out past it; the other three sides are countryside
    const view = new THREE.Vector3(Math.cos(ELEVATION) / Math.SQRT2, Math.sin(ELEVATION), Math.cos(ELEVATION) / Math.SQRT2);
    this.sea = new Sea3D(sun.position, view);
    this.scene.add(this.sea.mesh);
    // roads, checkpoints and wreckage on the three land sides
    this.scene.add(this.outskirts.group);

  }

  /** The rim-lit version of a model material, made once per material and colour. */
  private rimMaterial(m: THREE.Material, color: number, strength: number): THREE.Material {
    const key = `${m.uuid}:${color}`;
    let r = this.rimmed.get(key);
    if (!r) { r = withRim(m, color, strength); this.rimmed.set(key, r); }
    return r;
  }

  /**
   * The dressing the first frame can do without: the grass tufts and the post-processing (bloom,
   * anti-aliasing). Called once the page has shown something, so the title screen is not waiting
   * on five thousand tufts and a render target.
   */
  finishInit(): void {
    if (this.finished) return;
    this.finished = true;
    this.plantTufts();
    if (POST) {
      // no MSAA on the half-float target: it doubled the frame time; FXAA at the end smooths edges instead
      const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType });
      const composer = this.composer = new EffectComposer(this.renderer, target);
      const size = this.renderer.getSize(new THREE.Vector2());
      this.composerRatio = this.pixelRatio();
      composer.setPixelRatio(this.composerRatio);
      composer.setSize(size.x, size.y);
      composer.addPass(new RenderPass(this.scene, this.camera));
      composer.addPass(this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 0.55, 0.4, 1.6));
      composer.addPass(new OutputPass());
      composer.addPass(new FXAAPass());
    }
  }

  private pixelRatio(): number {
    return Math.min(devicePixelRatio, PIXEL_RATIO_CAP);
  }

  /** A tower not yet placed: every mesh under the clone goes see-through in one colour, green where it can go and red where it cannot. */
  private ghost(o: THREE.Object3D, color: number): void {
    let mat = this.ghostMats.get(color);
    if (!mat) {
      mat = new THREE.MeshLambertMaterial({ color, transparent: true, opacity: 0.55, depthWrite: false, emissive: new THREE.Color(color).multiplyScalar(0.35) });
      mat.userData.shared = true;
      this.ghostMats.set(color, mat);
    }
    o.traverse(c => {
      if (!(c instanceof THREE.Mesh)) return;
      // the worn ground under a built tower would read as a coloured square here
      if (c.name === 'groundPatch') { c.visible = false; return; }
      c.material = mat!;
      c.castShadow = false;
    });
  }

  /** Swaps every mesh material under a model clone for its rim-lit version. */
  private rim(o: THREE.Object3D, color: number, strength: number): void {
    o.traverse(c => {
      if (!(c instanceof THREE.Mesh) || c.userData.noRim) return;
      c.material = Array.isArray(c.material)
        ? c.material.map(m => this.rimMaterial(m, color, strength))
        : this.rimMaterial(c.material as THREE.Material, color, strength);
    });
  }

  /** Keeps the sun's shadow box over what is on screen, snapped to whole shadow texels so edges don't crawl as the view pans. */
  private fitShadow(cx: number, cz: number, hw: number, hh: number): void {
    // the screen's height covers twice as much ground at a 30 degree view
    const r = Math.min(Math.hypot(hw, hh * 2) * 1.1, 90);
    const cam = this.sun.shadow.camera;
    const texel = (2 * r) / SHADOW_MAP;
    const centre = new THREE.Vector3(cx, 0, cz);
    // step the centre to the shadow map's texel grid, in the light's own frame
    const look = new THREE.Matrix4().lookAt(SUN_DIR, new THREE.Vector3(), new THREE.Vector3(0, 1, 0));
    const inv = look.clone().invert();
    centre.applyMatrix4(inv);
    centre.x = Math.round(centre.x / texel) * texel;
    centre.y = Math.round(centre.y / texel) * texel;
    centre.applyMatrix4(look);
    this.sun.target.position.copy(centre);
    this.sun.position.copy(centre).addScaledVector(SUN_DIR, 100);
    this.sun.target.updateMatrixWorld();
    if (cam.right !== r) {
      cam.left = cam.bottom = -r;
      cam.right = cam.top = r;
      cam.updateProjectionMatrix();
    }
  }

  /** Rebuilds the ground for the current view angle. */
  /** The land the village has not claimed: shaded, with the claim's edge drawn; nothing when the whole plan is claimed or in a fight. */
  private syncClaim(): void {
    const c = claim3D();
    const key = c ? `${c.x0},${c.y0},${c.x1},${c.y1}@${this.builtAngle}` : '';
    if (key === this.claimKey) return;
    this.claimKey = key;
    if (this.claimGroup) {
      this.scene.remove(this.claimGroup);
      this.claimGroup.traverse(o => { const m = o as THREE.Mesh; m.geometry?.dispose(); (m.material as THREE.Material | undefined)?.dispose?.(); });
      this.claimGroup = null;
    }
    if (!c) return;
    const lo = PLACE_MARGIN, hi = MAP_SIZE - PLACE_MARGIN;
    if (c.x0 <= lo && c.y0 <= lo && c.x1 >= hi && c.y1 >= hi) return;
    const group = new THREE.Group();
    // four bands round the claim, over the land inside the plan
    const bands: [number, number, number, number][] = [[lo, lo, hi, c.y0], [lo, c.y1, hi, hi], [lo, c.y0, c.x0, c.y1], [c.x1, c.y0, hi, c.y1]];
    const pos: number[] = [], idx: number[] = [];
    for (const [x0, y0, x1, y1] of bands) {
      if (x1 <= x0 || y1 <= y0) continue;
      const k = pos.length / 3;
      for (const [tx, ty] of [[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as const) { const [x, y, z] = ground(tx, ty, 0.03); pos.push(x, y, z); }
      idx.push(k, k + 2, k + 1, k, k + 3, k + 2);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    const shade = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x06090a, transparent: true, opacity: 0.4, depthWrite: false }));
    shade.renderOrder = 1;
    group.add(shade);
    const ring: number[] = [];
    for (const [tx, ty] of [[c.x0, c.y0], [c.x1, c.y0], [c.x1, c.y1], [c.x0, c.y1], [c.x0, c.y0]] as const) { const [x, y, z] = ground(tx, ty, 0.05); ring.push(x, y, z); }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(ring, 3));
    const line = new THREE.Line(lineGeo, new THREE.LineBasicMaterial({ color: 0xe0b341, transparent: true, opacity: 0.85 }));
    line.renderOrder = 2;
    group.add(line);
    this.scene.add(group);
    this.claimGroup = group;
  }

  /** Points the floor at the map's road, or at none (the highways alone) when there is no map. */
  private syncRoad(): void {
    const map = road3D();
    const key = map ? map.id : '';
    if (key === this.roadKey) return;
    this.roadKey = key;
    setFloorRoad(this.groundMesh.material as THREE.MeshStandardMaterial, map);
    // the grass comes off the road
    this.clearTufts(buildings3D());
  }

  private buildGround(): void {
    // the floor runs past the map on the land sides (and under the water's edge on the west); its uv is tile coordinates
    const P = FLOOR_PAD;
    const corners: [number, number][] = [[-1, -P], [MAP_SIZE + P, -P], [MAP_SIZE + P, MAP_SIZE + P], [-1, MAP_SIZE + P]];
    const pos: number[] = [], uv: number[] = [];
    for (const [tx, ty] of corners) {
      const [x, y, z] = ground(tx, ty);
      pos.push(x, y, z);
      uv.push(tx, ty);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex([0, 2, 1, 0, 3, 2]);
    geo.computeVertexNormals();
    geo.computeBoundingSphere();
    this.groundMesh.geometry.dispose();
    this.groundMesh.geometry = geo;

    const M = COUNTRY_MARGIN;
    // the countryside runs right round, and on the west it is the channel's far bank, at ground level beyond the water
    const far: [number, number][] = [[-M, -M], [MAP_SIZE + M, -M], [MAP_SIZE + M, MAP_SIZE + M], [-M, MAP_SIZE + M]];
    // the land closing the bay in: beyond its far shore, and to its north and south, level with the ground
    const banks: [number, number, number, number][] = [
      [-M, -M, -SEA_OUT, MAP_SIZE + M],
      [-SEA_OUT, -M, 0, 0],
      [-SEA_OUT, MAP_SIZE, 0, MAP_SIZE + M],
    ];
    const bpos: number[] = [], buv: number[] = [], bidx: number[] = [];
    for (const [x0, y0, x1, y1] of banks) {
      const k = bpos.length / 3;
      for (const [tx, ty] of [[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as const) {
        const [x, y, z] = ground(tx, ty, 0.02);
        bpos.push(x, y, z);
        buv.push(tx / COUNTRY_REPEAT, ty / COUNTRY_REPEAT);
      }
      bidx.push(k, k + 2, k + 1, k, k + 3, k + 2);
    }
    const bgeo = new THREE.BufferGeometry();
    bgeo.setAttribute('position', new THREE.Float32BufferAttribute(bpos, 3));
    bgeo.setAttribute('uv', new THREE.Float32BufferAttribute(buv, 2));
    bgeo.setIndex(bidx);
    bgeo.computeVertexNormals();
    this.farBank.geometry.dispose();
    this.farBank.geometry = bgeo;
    const fpos: number[] = [], fuv: number[] = [];
    for (const [tx, ty] of far) {
      const [x, y, z] = ground(tx, ty);
      fpos.push(x, y, z);
      fuv.push(tx / COUNTRY_REPEAT, ty / COUNTRY_REPEAT);
    }
    const fgeo = new THREE.BufferGeometry();
    fgeo.setAttribute('position', new THREE.Float32BufferAttribute(fpos, 3));
    fgeo.setAttribute('uv', new THREE.Float32BufferAttribute(fuv, 2));
    fgeo.setIndex([0, 2, 1, 0, 3, 2]);
    fgeo.computeVertexNormals();
    this.countryside.geometry.dispose();
    this.countryside.geometry = fgeo;
    this.sea.rebuild(ground, viewAngle());
    this.outskirts.setAngle(viewAngle());
    this.tufts.rotation.y = -viewAngle();
  }

  /** Strews grass clumps and pebbles over the whole map, in tile space (the group turns with the view). */
  private plantTufts(): void {
    const r = seeded(19);
    const tuft = grassTuft();
    const grass = new THREE.InstancedMesh(tuft.geometry, tuft.material, Math.round(MAP_SIZE * MAP_SIZE * 0.5));
    const stones = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.05, 0), new THREE.MeshLambertMaterial({ flatShading: true }), Math.round(MAP_SIZE * MAP_SIZE * 0.15));
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), v = new THREE.Vector3(), sc = new THREE.Vector3(), col = new THREE.Color();
    const kinds: [THREE.InstancedMesh, number[], boolean][] = [
      [grass, [0x7c9c48, 0x6e9044, 0x88a452, 0x668a3e], false],
      [stones, [0x7d786c, 0x6a665c, 0x8e8878], true],
    ];
    for (const [mesh, colors, stone] of kinds) {
      const spots: [number, number][] = [];
      for (let i = 0; i < mesh.count; i++) {
        const x = 0.3 + r() * (MAP_SIZE - 0.6), y = 0.3 + r() * (MAP_SIZE - 0.6);
        spots.push([x, y]);
        const s = stone ? 0.5 + r() * 1.2 : 0.7 + r() * 0.8;
        e.set(stone ? r() * 3 : 0, r() * Math.PI * 2, stone ? r() * 3 : 0);
        m.compose(v.set(x - C, stone ? 0.01 : 0, y - C), q.setFromEuler(e), sc.set(s, stone ? s * 0.6 : s, s));
        mesh.setMatrixAt(i, m);
        mesh.setColorAt(i, col.set(colors[Math.floor(r() * colors.length)]!));
      }
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      mesh.userData.spots = spots;
      mesh.userData.full = Float32Array.from(mesh.instanceMatrix.array);
      this.tufts.add(mesh);
    }
  }

  /** Hides the clumps and pebbles that would poke up through a building. */
  private clearTufts(list: readonly DrawableBuilding[]): void {
    for (const mesh of this.tufts.children as THREE.InstancedMesh[]) {
      const spots = mesh.userData.spots as [number, number][];
      const arr = mesh.instanceMatrix.array as Float32Array;
      arr.set(mesh.userData.full as Float32Array);
      const map = road3D();
      const onRoad = (x: number, y: number): boolean => {
        if (!map) return false;
        for (const road of roadsOf(map)) for (let i = 1; i < road.length; i++) {
          const [ax, ay] = road[i - 1]!, [bx, by] = road[i]!;
          const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
          const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / len2));
          if (Math.hypot(x - (ax + dx * t), y - (ay + dy * t)) <= 1.6) return true;
        }
        return false;
      };
      spots.forEach(([x, y], i) => {
        if (onRoad(x, y) || list.some(b => x > b.x - 0.1 && x < b.x + b.size + 0.1 && y > b.y - 0.1 && y < b.y + b.size + 0.1)) arr.fill(0, i * 16, i * 16 + 16);
      });
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** Rebuilds the building boxes when the list or the view angle has changed. */
  private buildBuildings(): void {
    // only what changed is built again: the list changes each time a building is hit hard or falls,
    // and rebuilding all of them each time was a hitch of ten milliseconds or more in a big fight
    const old = this.built;
    this.built = new Map();
    this.fxByBuilding.clear();
    const a = viewAngle();
    const now = performance.now();
    const present = new Set<string>();
    const barriers = new Set(buildings3D().filter(b => b.type === 'barrier').map(b => `${b.x},${b.y}`));
    const footprints = buildings3D().map(b => `${b.x},${b.y},${b.size}`).join(';');
    if (footprints !== this.tuftsFor) {
      this.clearTufts(buildings3D());
      this.tuftsFor = footprints;
    }
    for (const b of buildings3D()) {
      const dead = b.state === 'dead';
      const [cx, , cz] = ground(b.x + b.size / 2, b.y + b.size / 2);
      // a destroyed building is a heap of rubble sized to its footprint
      const rubble = RUBBLE_MODELS.find(([n]) => n >= b.size) ?? RUBBLE_MODELS[RUBBLE_MODELS.length - 1]!;
      const model = this.models.get(dead ? rubble[1] : b.type);
      let top = 0.5 + b.size * 0.4;
      // a tower being placed is one thing wherever it hovers, so it never shares a real tower's entry
      const key = b.state === 'ghost-ok' || b.state === 'ghost-bad' ? 'ghost' : `${b.x},${b.y}`;
      present.add(key);
      const was = this.wasDead.get(key);
      this.wasDead.set(key, dead);
      if (dead && was === false) {
        this.blowUp(b, now);
        this.fx.wreck(new THREE.Vector3(cx, 0.2, cz), 0.8 + b.size * 0.35);
      } else if (!dead && was) {
        // standing again (a new raid): sweep up its rubble
        this.debris.clear(key);
        this.diedAt.delete(key);
      }
      // everything this building's meshes depend on; the place too, since the ghost keeps one key wherever it hovers
      const sig = [
        b.type, b.x, b.y, b.size, dead, b.hpFrac < 0.6, b.hpFrac < 0.5, b.seed, a, !!model, b.state ?? '',
        b.type === 'barrier' ? JSON.stringify(barrierPieces(b.x, b.y, barriers)) : '',
        b.type === 'sniper_nest' && !!this.models.get('marksman'),
        b.type === 'airbase' && !!this.models.get('carrier'),
      ].join('|');
      const kept = old.get(key);
      if (kept && kept.sig === sig) {
        old.delete(key);
        this.built.set(key, kept);
        if (kept.fx) this.fxByBuilding.set(key, kept.fx);
        continue;
      }
      const start = this.buildingGroup.children.length;
      const fx: BuildingFx = { puffs: [], top, seed: b.seed ?? (b.x * 97 + b.y * 31) };
      if (model) {
        // the model is square to the map, so turn it with the view; a barrier is laid along the way it joins its neighbours
        const pieces: (BarrierPiece | null)[] = b.type === 'barrier' && !dead ? barrierPieces(b.x, b.y, barriers) : [null];
        for (const piece of pieces) {
          const m = model.clone();
          if (piece) {
            const [px, , pz] = ground(b.x + 0.5 + piece.shift * piece.ax, b.y + 0.5 + piece.shift * piece.ay);
            m.position.set(px, 0, pz);
            m.rotation.y = barrierTurn(piece.ax, piece.ay);
            m.scale.x *= piece.length;
          } else {
            m.position.set(cx, 0, cz);
            m.rotation.y = -a;
          }
          if (dead) {
            m.scale.setScalar(b.size / rubble[0]);
            if (this.diedAt.has(key)) this.heaps.push({ obj: m, full: m.scale.x, key });
          } else if (b.state === 'ghost-ok' || b.state === 'ghost-bad') this.ghost(m, b.state === 'ghost-ok' ? 0x5ad66a : 0xe8463a);
          else this.rim(m, BUILDING_RIM, 0.25);
          this.buildingGroup.add(m);
          top = fx.top = (model.userData.height as number) * m.scale.y;
          m.traverse(o => {
            if (o.userData.axis) fx.spin = { node: o, axis: o.userData.axis as Axis, base: o.rotation[o.userData.axis as Axis] };
          });
          const head = !dead && TURRETS[b.type] ? m.getObjectByName('head') : undefined;
          if (head) {
            // the C-RAM's barrels ride on its head
            const barrels = m.getObjectByName('barrels');
            if (barrels) head.attach(barrels);
            fx.head = { node: head, base: head.position.clone() };
          }
          if (!dead) fx.radar = m.getObjectByName('radar');
          const beacon = dead ? undefined : m.getObjectByName('beacon');
          if (beacon) fx.beacon = { node: beacon, hurt: b.hpFrac < 0.5 };
        }
      } else {
        const color = dead ? RUBBLE : (COLORS[b.type] ?? 0x8a8a80);
        // a destroyed building is a low heap of rubble
        const h = dead ? 0.14 : top;
        const mesh = new THREE.Mesh(
          new THREE.BoxGeometry(b.size, h, b.size),
          new THREE.MeshLambertMaterial({ color }),
        );
        mesh.position.set(cx, h / 2, cz);
        mesh.castShadow = mesh.receiveShadow = true;
        // the box is square to the rotated map, so turn it with the view
        mesh.rotation.y = -a;
        this.buildingGroup.add(mesh);
      }
      if (dead) {
        this.built.set(key, { sig, objs: this.buildingGroup.children.slice(start), fx: null });
        continue;
      }

      if (b.hpFrac < 0.6) {
        for (let k = 0; k < 3; k++) {
          const puff = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 6), new THREE.MeshLambertMaterial({ color: 0x444444, transparent: true, depthWrite: false }));
          this.buildingGroup.add(puff);
          fx.puffs.push(puff);
        }
      }
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(b.size * 0.7, 0.04, 6, 32),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(EMP_BLUE).multiplyScalar(GLOW), transparent: true, depthWrite: false }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(cx, 0.05, cz);
      ring.visible = false;
      this.buildingGroup.add(ring);
      fx.ring = ring;
      if (b.type === 'c_ram' && !model) {
        const barrels = new THREE.Group();
        barrels.position.set(cx, top, cz);
        for (let k = 0; k < 6; k++) {
          const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.5, 6), this.material(0x2b2f33));
          barrel.position.y = 0.25;
          const pivot = new THREE.Group();
          pivot.rotation.x = (k * Math.PI) / 3;
          pivot.add(barrel);
          barrels.add(pivot);
        }
        this.buildingGroup.add(barrels);
        fx.spin = { node: barrels, axis: 'x', base: 0 };
      }
      // the sniper nest's marksman stands behind its parapet, turned a little differently in each nest
      const marksman = b.type === 'sniper_nest' && model ? this.models.get('marksman') : null;
      if (marksman) {
        const guy = cloneModel(marksman);
        guy.scale.multiplyScalar(SNIPER_SCALE);
        guy.position.copy(SNIPER_SPOT).applyAxisAngle(THREE.Object3D.DEFAULT_UP, -a).add(new THREE.Vector3(cx, 0, cz));
        const base = -a + rnd(fx.seed, 1) * Math.PI * 2;
        guy.rotation.y = base;
        this.rim(guy, BUILDING_RIM, 0.25);
        // the model carries its rifle low at its side, out of sight from above, so he also shoulders a long scoped anti-materiel rifle
        const gun = this.material(0x23272b);
        const steel = this.material(0x4c545c);
        const wood = this.material(0x5c3820);
        const lens = this.material(0x2980b9);
        const rifle = (geo: THREE.BufferGeometry, x: number, y: number, z: number, mat = gun) => {
          const part = new THREE.Mesh(geo, mat);
          part.position.set(x, y, z);
          part.castShadow = true;
          guy.add(part);
          return part;
        };
        rifle(box(0.24, 0.038, 0.032), 0.06, 0.468, 0.06, gun);
        rifle(box(0.22, 0.032, 0.028), 0.24, 0.468, 0.06, gun);
        rifle(new THREE.CylinderGeometry(0.011, 0.013, 0.48, 8).rotateZ(Math.PI / 2), 0.42, 0.472, 0.06, steel);
        rifle(box(0.055, 0.026, 0.032), 0.67, 0.472, 0.06, gun);
        rifle(box(0.17, 0.058, 0.032), -0.10, 0.452, 0.06, wood);
        rifle(box(0.055, 0.055, 0.024), 0.11, 0.425, 0.06, steel);
        rifle(new THREE.CylinderGeometry(0.016, 0.016, 0.16, 10).rotateZ(Math.PI / 2), 0.10, 0.508, 0.06, gun);
        rifle(new THREE.CylinderGeometry(0.024, 0.018, 0.055, 10).rotateZ(Math.PI / 2), 0.19, 0.508, 0.06, gun);
        rifle(new THREE.CylinderGeometry(0.020, 0.020, 0.008, 10).rotateZ(Math.PI / 2), 0.218, 0.508, 0.06, lens);
        this.buildingGroup.add(guy);
        // he breathes and shifts his weight in the aiming stance the shouldered rifle is placed for
        fx.sniper = { node: guy, home: guy.position.clone(), base, aim: base, kick: 0, shot: null, firedAt: -Infinity, motion: new UnitMotion(guy, 'foot', 'marksman', fx.seed), last: now };
      }
      // the HQ model has its own flag
      if (b.type === 'hq' && !model) {
        const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.6, 0.3), new THREE.MeshLambertMaterial({ color: FLAG_COLOR, side: THREE.DoubleSide }));
        flag.position.set(cx + 0.3, top + 0.9, cz);
        this.buildingGroup.add(flag);
        fx.flag = flag;
      }
      // a carrier air wing's fighters fly off its carrier, standing offshore level with the post
      if (b.type === 'airbase') {
        const cv = this.models.get('carrier');
        if (cv) {
          const m = cv.clone();
          const [X, , Z] = ground(-STANDOFF + 0.5, b.y + b.size / 2);
          m.position.set(X, 0, Z);
          m.rotation.y = -a + Math.PI / 2;
          this.rim(m, BUILDING_RIM, 0.25);
          this.buildingGroup.add(m);
        }
      }
      this.fxByBuilding.set(key, fx);
      this.built.set(key, { sig, objs: this.buildingGroup.children.slice(start), fx });
    }
    // what was built before and is not kept: gone, or changed and built again above
    for (const gone of old.values()) {
      for (const o of gone.objs) {
        this.buildingGroup.remove(o);
        disposeTree(o);
      }
    }
    this.heaps = this.heaps.filter(h => h.obj.parent === this.buildingGroup);
    for (const key of [...this.wasDead.keys()]) {
      if (present.has(key)) continue;
      this.wasDead.delete(key);
      this.diedAt.delete(key);
      this.debris.clear(key);
    }
    this.growHeaps(now);
  }

  /** A building has just been destroyed: blow it apart in its own materials. */
  private blowUp(b: DrawableBuilding, now: number): void {
    const key = `${b.x},${b.y}`;
    const [cx, , cz] = ground(b.x + b.size / 2, b.y + b.size / 2);
    const whole = this.models.get(b.type);
    const mats = new Set<THREE.Material>();
    whole?.traverse(o => {
      if (o instanceof THREE.Mesh) for (const m of [o.material as THREE.Material | THREE.Material[]].flat()) mats.add(m);
    });
    const height = whole ? (whole.userData.height as number) : 0.5 + b.size * 0.4;
    // a building going down throws what is already lying round it, then bursts into its own chunks
    this.kick(new THREE.Vector3(cx, 0, cz), b.size * 0.9 + 1, 4);
    this.debris.explode(key, [...mats], new THREE.Vector3(cx, 0, cz), b.size, height, b.seed ?? (b.x * 97 + b.y * 31), now);
    // brought down by a nuke: its rubble is hurled outward from ground zero, not just tumbled where it stood
    const nuke = this.lastNuke;
    if (nuke && now - nuke.when < 1500 && Math.hypot(cx - nuke.at.x, cz - nuke.at.z) < nuke.r * 1.6) this.debris.kick(nuke.at, nuke.r * 1.6, 14);
    this.diedAt.set(key, now);
  }

  /** Raises each fresh rubble heap out of the ground over its first moments. */
  private growHeaps(now: number): void {
    this.heaps = this.heaps.filter(h => {
      const t = Math.min(1, (now - this.diedAt.get(h.key)!) / HEAP_MS);
      const e = 1 - (1 - t) * (1 - t);
      h.obj.scale.set(h.full, h.full * (0.05 + 0.95 * e), h.full);
      return t < 1;
    });
  }

  /** Smoke from damaged buildings, the EMP ring, the C-RAM barrels and the HQ flag. */
  private syncBuildingFx(now: number): void {
    for (const b of buildingFx3D() as readonly DrawableBuilding[]) {
      const fx = this.fxByBuilding.get(`${b.x},${b.y}`);
      if (!fx) continue;
      const top = fx.top;
      for (let k = 0; k < fx.puffs.length; k++) {
        const puff = fx.puffs[k]!;
        const phase = (now / 900 + rnd(fx.seed, k)) % 1;
        const [px, , pz] = ground(b.x + b.size / 2 + (rnd(fx.seed, k + 5) - 0.5) * 0.5, b.y + b.size / 2 + (rnd(fx.seed, k + 9) - 0.5) * 0.5);
        puff.position.set(px, top + phase * 1.2, pz);
        puff.scale.setScalar(0.5 + phase * 0.8);
        (puff.material as THREE.MeshLambertMaterial).opacity = 0.5 * (1 - phase);
      }
      if (fx.ring) {
        fx.ring.visible = !!b.disabled;
        (fx.ring.material as THREE.MeshBasicMaterial).opacity = 0.45 + 0.4 * Math.sin(now / 70);
      }
      if (fx.spin) {
        // C-RAM barrels: still at rest, spun up in about a quarter of a second to 750 rpm (4,500 rounds a
        // minute over six barrels) while firing, and coasting down after; turbine blades turn steadily
        const sp = fx.spin;
        const want = b.type === 'c_ram' ? (b.firing ? CRAM_SPIN : 0) : 0.002;
        const dt = Math.min(100, now - (sp.last ?? now));
        sp.last = now;
        const ease = b.type === 'c_ram' ? (want > (sp.rate ?? 0) ? 0.012 : 0.004) : 1;
        sp.rate = (sp.rate ?? want) + (want - (sp.rate ?? want)) * Math.min(1, dt * ease);
        sp.angle = (sp.angle ?? fx.seed) + sp.rate * dt;
        sp.node.rotation[sp.axis] = sp.base + sp.angle;
      }
      if (fx.flag) fx.flag.rotation.y = Math.sin(now / 300) * 0.3 - Math.PI / 2;
      if (fx.sniper) this.aimSniper(fx.sniper, b, now);
      if (fx.head) this.aimTurret(fx.head, b, now);
      if (fx.radar) fx.radar.rotation.y = now * 0.0015 + fx.seed;
      if (fx.beacon) {
        // a slow on-off blink; a battered command post's lights stutter and drop out
        const t = now / 700 + fx.seed;
        fx.beacon.node.visible = fx.beacon.hurt ? Math.sin(now / 37) * Math.sin(now / 91 + fx.seed) > 0.15 : t % 1 < 0.55;
      }
    }
  }

  /** The newest shot fired from inside a building's footprint. */
  private shotFrom(b: DrawableBuilding): LiveShot | null {
    const x0 = b.x * TILE, y0 = b.y * TILE, x1 = (b.x + b.size) * TILE, y1 = (b.y + b.size) * TILE;
    let best: LiveShot | null = null;
    for (const sh of shots3D()) {
      if (sh.by !== 'defence' || sh.type !== b.type) continue;
      if (sh.fromX < x0 || sh.fromX >= x1 || sh.fromY < y0 || sh.fromY >= y1) continue;
      if (!best || sh.at > best.at) best = sh;
    }
    return best;
  }

  /**
   * Swings a turret head onto its newest shot (slewing at the turret's own speed), kicks it back
   * along the barrel as it fires and records where the muzzle was; idle, it sweeps the field.
   */
  private aimTurret(h: NonNullable<BuildingFx['head']>, b: DrawableBuilding, now: number): void {
    // the list can run a frame ahead of the meshes (a different base loading in)
    const spec = TURRETS[b.type];
    if (!spec) return;
    const key = `${b.x},${b.y}`;
    let st = this.turrets.get(key);
    if (!st) { st = { yaw: 0, scan: rnd(b.x, b.y) * Math.PI * 2, kick: 0, firedAt: -Infinity, last: now, shot: null }; this.turrets.set(key, st); }
    const dt = Math.min(0.1, Math.max(0, (now - st.last) / 1000));
    st.last = now;
    const a = viewAngle();
    const shot = this.shotFrom(b);
    let want: number;
    if (shot && now - shot.at < 1500) {
      const [fX, , fZ] = ground(sh2t(shot.fromX), sh2t(shot.fromY));
      const [tX, , tZ] = ground(sh2t(shot.toX), sh2t(shot.toY));
      // the head's own turn that puts its gun on the target, the model being turned by -a
      want = Math.atan2(-(tZ - fZ), tX - fX) + a - spec.rest;
    } else {
      // nothing to shoot: a slow, uneven sweep
      st.scan += dt * 0.35 * (0.6 + 0.4 * Math.sin(now / 1700 + b.x));
      want = st.scan + Math.sin(now / 900 + b.y) * 0.25;
    }
    const d = Math.atan2(Math.sin(want - st.yaw), Math.cos(want - st.yaw));
    st.yaw += THREE.MathUtils.clamp(d, -spec.slew * dt, spec.slew * dt);
    if (!(shot && now - shot.at < 1500)) st.scan = st.yaw - Math.sin(now / 900 + b.y) * 0.25;
    if (shot && shot !== st.shot) {
      st.shot = shot;
      st.kick = 1;
      st.firedAt = now;
    }
    st.kick *= Math.exp(-dt * 14);
    const node = h.node;
    const vib = b.type === 'c_ram' && b.firing ? Math.sin(now * 0.14) * 0.009 : 0;
    const climb = st.kick * 0.06 + vib;
    node.rotation.set(climb * Math.sin(spec.rest), st.yaw, climb * Math.cos(spec.rest));
    // back along the barrel, in the head's parent's frame
    const back = new THREE.Vector3(Math.cos(st.yaw + spec.rest), 0, -Math.sin(st.yaw + spec.rest));
    node.position.copy(h.base).addScaledVector(back, -(st.kick * st.kick + Math.abs(vib) * 0.5) * spec.kick);
    if (shot && !this.seenShots.has(shot) && !this.muzzles.has(shot)) {
      node.updateWorldMatrix(true, false);
      const p = new THREE.Vector3().setFromMatrixPosition(node.matrixWorld);
      const worldYaw = st.yaw + spec.rest - a;
      p.x += Math.cos(worldYaw) * spec.reach;
      p.z -= Math.sin(worldYaw) * spec.reach;
      p.y += spec.up;
      this.muzzles.set(shot, p);
    }
  }

  /** Turns the marksman toward the shot he just fired, kicks back on fire, holds there a moment, then sweeps slowly across the field. */
  private aimSniper(s: NonNullable<BuildingFx['sniper']>, b: DrawableBuilding, now: number): void {
    const dt = Math.min(0.1, Math.max(0, (now - s.last) / 1000));
    const shot = this.shotFrom(b);
    let fired = false;
    if (shot && now - shot.at < 1500) {
      const [aX, , aZ] = ground(shot.fromX / TILE, shot.fromY / TILE);
      const [bX, , bZ] = ground(shot.toX / TILE, shot.toY / TILE);
      s.aim = Math.atan2(-(bZ - aZ), bX - aX);
      if (shot !== s.shot) {
        s.shot = shot;
        s.kick = 1;
        s.firedAt = now;
        fired = true;
      }
      if (!this.seenShots.has(shot) && !this.muzzles.has(shot)) {
        // the muzzle of his shouldered rifle, out along his aim
        const p = s.home.clone();
        p.y += 0.49 * SNIPER_SCALE;
        p.x += Math.cos(s.aim) * 0.65;
        p.z -= Math.sin(s.aim) * 0.65;
        this.muzzles.set(shot, p);
      }
    }
    s.kick *= Math.exp(-dt * 12);
    const want = now - s.firedAt < SNIPER_HOLD ? s.aim : s.base + Math.sin(now / 2600 + s.base) * 0.9;
    const r = s.node.rotation;
    r.y += Math.atan2(Math.sin(want - r.y), Math.cos(want - r.y)) * (fired || now - s.firedAt < 300 ? 0.35 : 0.06);
    r.z = s.kick * 0.08;
    const slam = s.kick * s.kick * 0.075;
    s.node.position.set(s.home.x - Math.cos(r.y) * slam, s.home.y, s.home.z + Math.sin(r.y) * slam);
    s.motion.update({ now, dt, speed: 0, turn: 0, aim: now - s.firedAt < SNIPER_HOLD ? 0 : null, fired });
    s.last = now;
  }

  /** Copies the Pixi world's pan and zoom, so the 3D scene lines up under the 2D one. */
  sync(app: Application, world: Container): void {
    const W = app.screen.width, H = app.screen.height;
    const dpr = this.pixelRatio();
    if (this.renderer.domElement.width !== Math.round(W * dpr) || this.renderer.domElement.height !== Math.round(H * dpr) || (this.composer && this.composerRatio !== dpr)) {
      this.renderer.setPixelRatio(dpr);
      this.renderer.setSize(W, H, false);
      if (this.composer) {
        this.composerRatio = dpr;
        this.composer.setPixelRatio(dpr);
        this.composer.setSize(W, H);
      }
    }
    this.renderer.domElement.style.width = `${W}px`;
    this.renderer.domElement.style.height = `${H}px`;

    if (this.builtAngle !== viewAngle()) {
      this.buildGround();
      this.builtAngle = viewAngle();
      this.builtVersion = -1;
    }
    this.syncClaim();
    this.syncRoad();
    // rebuild when the list changes or a model has arrived to replace a box
    const v = buildingsVersion();
    if (v !== this.builtVersion || this.models.version !== this.builtModels) {
      this.builtModels = this.models.version;
      this.buildBuildings();
      this.builtVersion = v;
    }

    // which tile sits at the middle of the screen
    const s = world.scale.x;
    const [tx, ty] = toTile((W / 2 - world.position.x) / s, (H / 2 - world.position.y) / s);
    const [cx, cy, cz] = ground(tx, ty);
    const k = PX_PER_UNIT * s;
    const hw = W / (2 * k), hh = H / (2 * k);
    this.camera.left = -hw; this.camera.right = hw;
    this.camera.top = hh; this.camera.bottom = -hh;
    // camera sits up and out along the view direction; lookAt does the rest
    const dist = 500;
    const off = new THREE.Vector3(Math.cos(ELEVATION) / Math.SQRT2, Math.sin(ELEVATION), Math.cos(ELEVATION) / Math.SQRT2);
    this.camera.position.set(cx + off.x * dist, off.y * dist, cz + off.z * dist);
    this.camera.lookAt(cx, 0, cz);
    // big blasts shake the view along the screen's own right and up
    const [sx, sy] = this.fx.shake(performance.now());
    if (sx || sy) {
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
      const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
      this.camera.position.addScaledVector(right, sx).addScaledVector(up, sy);
    }
    this.camera.updateProjectionMatrix();
    this.fitShadow(cx, cz, hw, hh);

    const now = performance.now();
    this.syncUnits(now);
    this.crowd.update(now);
    this.syncBuildingFx(now);
    this.sea.update(now);
    this.outskirts.update(now);
    this.syncShots(now);
    this.syncDeaths(now);
    this.syncSmoke(now);
    for (const s of incoming3D()) {
      const [X, , Z] = ground(s.x + 0.5, s.y + 0.5);
      this.fx.marker(`${s.kind}:${s.x},${s.y}`, new THREE.Vector3(X, 0, Z), STRIKE_RADIUS[s.kind], s.kind, s.left / TICKS_PER_SECOND);
    }
    this.debris.update(now);
    this.bodies.update(now);
    this.fx.update(now, this.camera);
    this.growHeaps(now);

    this.syncWhiteout(now);
    this.gpuTimer.begin();
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
    this.gpuTimer.end();
  }

  private material(color: number): THREE.MeshLambertMaterial {
    let m = this.materials.get(color);
    if (!m) { m = new THREE.MeshLambertMaterial({ color }); m.userData.shared = true; this.materials.set(color, m); }
    return m;
  }

  /** One unit: its loaded model rimmed in team colour, or a simple stand-in shape while the model loads. */
  /**
   * A unit's group, placed and turned by syncUnits, and its motion (unitmotion.ts). A machine
   * gets a copy of its model; a soldier joins the crowd of his type (crowd3d.ts), and his group
   * holds nothing but his selection ring, the crowd drawing him where the group is.
   */
  private makeUnit(id: number, type: string, kind: string, color: number, lift: number, defender: boolean): THREE.Group {
    const g = new THREE.Group();
    // what it is, for its death: a man becomes a ragdoll in his side's colours, a machine a wreck
    g.userData.kind = kind;
    g.userData.color = color;
    const model = this.models.get(type);
    let ringR = 0.5;
    if (model) {
      const team = defender ? TEAM.defender : TEAM.attacker;
      const scale = UNIT_SCALE[type] ?? KIND_SCALE[kind] ?? 1;
      const glow = Math.max(0.45, (model.userData.footprint as number) * scale * 1.1);
      ringR = Math.max(0.3, glow * 0.5);
      const member = kind === 'foot' ? this.crowd.member(type, model) : null;
      if (member) {
        member.setRim(team);
        g.userData.crowd = member;
        g.userData.scale = scale;
        this.motions.set(id, new UnitMotion(g, 'foot', type, id, member));
      } else {
        const m = cloneModel(model);
        m.scale.multiplyScalar(scale);
        this.rim(m, team, 0.8);
        g.add(m);
        this.motions.set(id, new UnitMotion(m, kind as 'foot', type, id));
      }
      g.userData.model = true;
    }
    const add = (geo: THREE.BufferGeometry, m: THREE.Material, x: number, y: number, z: number) => {
      const mesh = new THREE.Mesh(geo, m);
      mesh.position.set(x, y, z);
      g.add(mesh);
    };
    const mat = this.material(color);
    const parts = g.children.length;
    switch (model ? '' : kind) {
      case 'foot':
        add(new THREE.CylinderGeometry(0.1, 0.12, 0.42, 8), mat, 0, 0.21, 0);
        add(new THREE.SphereGeometry(0.1, 8, 6), this.material(SKIN), 0, 0.5, 0);
        break;
      case 'vehicle':
        add(box(0.8, 0.26, 0.5), mat, 0, 0.13, 0);
        add(box(0.4, 0.16, 0.36), mat, 0, 0.34, 0);
        add(box(0.45, 0.05, 0.05), mat, 0.42, 0.34, 0);
        break;
      case 'ship':
        add(box(0.95, 0.18, 0.4), mat, 0, 0.09, 0);
        add(box(0.3, 0.22, 0.3), mat, -0.15, 0.27, 0);
        break;
      case 'air':
        add(box(0.6, 0.2, 0.26), mat, 0, 0.12, 0);
        add(box(0.08, 0.02, 0.9), this.material(0x222222), 0, 0.3, 0);
        break;
      case 'drone':
        add(new THREE.SphereGeometry(0.13, 8, 6), mat, 0, 0, 0);
        break;
      case 'missile':
        add(box(0.12, 0.12, 0.6), mat, 0, 0.06, 0);
        break;
    }
    for (const c of g.children.slice(parts)) c.castShadow = true;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(ringR * 0.84, ringR, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthWrite: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.02 - lift;
    ring.name = 'ring';
    ring.visible = false;
    g.add(ring);
    return g;
  }

  private syncUnits(now: number): void {
    const dt = Math.min(0.1, Math.max(0, (now - (this.lastUnitFrame || now)) / 1000));
    this.lastUnitFrame = now;
    const u3 = units3D();
    const seen = new Set<number>();
    if (u3) {
      const prevById = new Map([...u3.prev.units, ...u3.prev.patrols, ...inFlight(u3.prev)].map(x => [x.id, x]));
      // the moment's shots by the type that fired them, so each unit looks through its own type's and not the whole field's
      const shotsByType = new Map<string, LiveShot[]>();
      for (const sh of shots3D()) {
        if (sh.by !== 'unit' || now - sh.at > 400) continue;
        let list = shotsByType.get(sh.type);
        if (!list) shotsByType.set(sh.type, list = []);
        list.push(sh);
      }
      const mat = new THREE.Matrix4();
      for (const u of [...u3.next.units, ...u3.next.patrols, ...inFlight(u3.next)]) {
        seen.add(u.id);
        const p = prevById.get(u.id) ?? u;
        const style = unit3DStyle(u);
        let g = this.units.get(u.id);
        // swap the stand-in for the model once it has arrived
        if (g && !g.userData.model && this.models.get(u.type)) {
          this.dropUnit(u.id, g);
          g = undefined;
        }
        if (!g) {
          g = this.makeUnit(u.id, u.type, style.kind, style.color, style.lift, !!u.defender);
          this.units.set(u.id, g);
          this.unitGroup.add(g);
        }
        // the same small spread as the 2D squads, so a squad reads as a squad; missiles fly exactly where the sim has them
        const exact = style.kind === 'missile';
        const jx = exact ? 0 : (((u.id * 37) % 7) - 3) * 0.03, jy = exact ? 0 : (((u.id * 53) % 5) - 2) * 0.03;
        const x = (p.x + (u.x - p.x) * u3.t) / TILE + jx, y = (p.y + (u.y - p.y) * u3.t) / TILE + jy;
        // a warhead in its terminal dive comes down out of the sky as its fuse runs out; a missile climbs and
        // falls on a high arc from its launcher to what it is locked on (an interceptor ends at its target's height)
        const targetY = (u as Flying).targetId !== undefined ? this.units.get((u as Flying).targetId!)?.position.y : undefined;
        // a missile that missed flies straight on at the height it was, climbing a little, until its self-destruct
        if ((u as Flying).coasting) g.userData.coastY = (g.userData.coastY ?? g.position.y) + 0.4 * dt;
        // a jet comes in low on its run and climbs hard away after it, easing between the two
        if (JETS.has(u.type)) g.userData.jetY = (g.userData.jetY ?? style.lift) + ((u.egress ? 3.4 : 1.1) - (g.userData.jetY ?? style.lift)) * Math.min(1, dt * 1.6);
        const lift = JETS.has(u.type) ? g.userData.jetY as number
          : (u as Flying).coasting ? g.userData.coastY as number
          : u.arc ? arcLift(u.arc, x, y, targetY ?? 0.5, u.type === 'icbm' ? 2.2 : u.type === 'hgv_nuclear' ? 1.4 : 1)
          : u.fuse !== undefined && u.fuseTicks ? 0.4 + (12 * (u.fuse - u3.t)) / u.fuseTicks
          // out beyond the map, marching in over the hills: on the ground there, not inside it
          : style.lift + (style.kind === 'missile' ? 0 : terrain(x, y));
        const [X, , Z] = ground(x, y, lift);
        // ground speed from how far it moved since the last frame, smoothed over a few frames
        const moved = g.userData.placed ? Math.hypot(X - g.position.x, Z - g.position.z) : 0;
        // a missile pitches with its arc: nose up as it climbs, down as it dives
        if (u.arc && g.userData.placed && moved > 1e-4) {
          const pitch = Math.atan2(lift - g.position.y, moved);
          g.rotation.z += (pitch - g.rotation.z) * Math.min(1, dt * 12);
        }
        const speed = g.userData.speed = dt > 0 ? (g.userData.speed ?? 0) * 0.8 + (moved / dt) * 0.2 : (g.userData.speed ?? 0);
        g.position.set(X, lift, Z);
        g.userData.placed = true;
        // where things in the air were last seen, so one shot down comes apart there
        if (style.kind === 'missile' || style.kind === 'air' || style.kind === 'drone') {
          const last = this.lastInAir.get(u.id);
          if (last) last.copy(g.position); else this.lastInAir.set(u.id, g.position.clone());
        }
        // what it has taken shows: a machine smokes and then burns, a wounded man bleeds
        const frac = u.hp / Math.max(1, u.maxHp);
        if (frac < 0.6 && style.kind !== 'missile') {
          const at = g.position.clone().setY(g.position.y + (style.kind === 'foot' ? 0.3 : 0.25));
          if (style.kind === 'foot') this.fx.bleeding(at, frac, dt); else this.fx.damaged(at, frac, dt);
        }
        // its latest shot: one fired from where it stands in the last moment
        let shot: LiveShot | null = null;
        for (const sh of shotsByType.get(u.type) ?? []) {
          if (Math.hypot(sh.fromX - u.x, sh.fromY - u.y) > TILE * 0.9) continue;
          if (!shot || sh.at > shot.at) shot = sh;
        }
        const heading = g.rotation.y;
        // face the way it is moving; a soldier standing to fire turns to his target
        const [aX, , aZ] = ground(u.x / TILE, u.y / TILE);
        const [bX, , bZ] = ground(p.x / TILE, p.y / TILE);
        let want: number | null = null;
        if (Math.hypot(aX - bX, aZ - bZ) > 1e-4) want = Math.atan2(-(aZ - bZ), aX - bX);
        let aimWorld: number | null = null;
        if (shot) {
          const [fX, , fZ] = ground(sh2t(shot.fromX), sh2t(shot.fromY));
          const [tX, , tZ] = ground(sh2t(shot.toX), sh2t(shot.toY));
          aimWorld = Math.atan2(-(tZ - fZ), tX - fX);
          if (style.kind === 'foot' && speed < 0.1) want = aimWorld;
        }
        if (want !== null) {
          // ease round to the new heading the short way, so a turn reads as a turn
          if (g.userData.faced) {
            const delta = Math.atan2(Math.sin(want - g.rotation.y), Math.cos(want - g.rotation.y));
            g.rotation.y += delta * Math.min(1, dt * 10);
          } else {
            g.rotation.y = want;
            g.userData.faced = true;
          }
        }
        const turnStep = Math.atan2(Math.sin(g.rotation.y - heading), Math.cos(g.rotation.y - heading));
        if (JETS.has(u.type)) {
          // a jet banks into its turns and pitches with its climbs and dives
          g.rotation.order = 'YZX';
          const bank = Math.max(-1.3, Math.min(1.3, -(dt > 0 ? turnStep / dt : 0) * 0.35));
          g.rotation.x += (bank - g.rotation.x) * Math.min(1, dt * 6);
          const climb = moved > 1e-4 ? Math.atan2(lift - (g.userData.lastY ?? lift), moved) : 0;
          g.rotation.z += (climb - g.rotation.z) * Math.min(1, dt * 6);
          g.userData.lastY = lift;
        }
        const motion = this.motions.get(u.id);
        if (motion) {
          const fired = !!shot && shot.at !== g.userData.shotAt;
          if (shot) g.userData.shotAt = shot.at;
          if (fired && aimWorld !== null) this.muzzles.set(shot!, this.unitMuzzle(g, style.kind, aimWorld));
          motion.update({
            now, dt, speed, turn: dt > 0 ? turnStep / dt : 0,
            aim: aimWorld === null ? null : Math.atan2(Math.sin(aimWorld - g.rotation.y), Math.cos(aimWorld - g.rotation.y)),
            fired,
          });
        }
        if (JETS.has(u.type) && speed > 0.05) {
          // the afterburner: a hot trail straight out of the back
          const c = Math.cos(g.rotation.z);
          const back = new THREE.Vector3(Math.cos(g.rotation.y) * c, Math.sin(g.rotation.z), -Math.sin(g.rotation.y) * c);
          this.fx.exhaust(u.id, g.position.clone().addScaledVector(back, -0.75), back);
        }
        if (style.kind === 'missile' && speed > 0.05) {
          // a missile lays its trail from its tail, along the way it is pointing
          const c = Math.cos(g.rotation.z);
          const dir = new THREE.Vector3(Math.cos(g.rotation.y) * c, Math.sin(g.rotation.z), -Math.sin(g.rotation.y) * c);
          const tail = (u as Flying).targetId !== undefined ? 0.35 : 0.62;
          this.fx.exhaust(u.id, g.position.clone().addScaledVector(dir, -tail).setY(g.position.y - dir.y * tail + 0.06), dir);
        }
        const ring = g.getObjectByName('ring');
        if (ring) ring.visible = u3.selected.has(u.id);
        // a soldier in the crowd is drawn where his group now is, kicked back slightly when his weapon fires
        const member = g.userData.crowd as CrowdMember | undefined;
        if (member) {
          const rk = (g.userData.recoilKick as number | undefined) ?? 0;
          if (rk > 1e-4) {
            const p = g.position.clone();
            p.x -= Math.cos(g.rotation.y) * rk;
            p.z += Math.sin(g.rotation.y) * rk;
            mat.compose(p, g.quaternion, g.scale);
          } else {
            mat.compose(g.position, g.quaternion, g.scale);
          }
          member.setMatrix(mat, g.userData.scale as number);
        }
      }
    }
    const deaths = deaths3D();
    for (const [id, g] of this.units) {
      if (seen.has(id)) continue;
      // killed by a nuke: whatever it is, man or tank, it is thrown away from ground zero across the map
      const nuked = u3 ? deaths.find(d => d.blast && now - d.at < 600 && (d.seed - 7) / 31 === id) : undefined;
      if (nuked) { this.fling(id, g, nuked, now); continue; }
      // a soldier who has just been killed falls where he stood before he goes
      const motion = this.motions.get(id);
      const killed = deaths.some(d => {
        if (d.kind !== 'flesh' || now - d.at > 600) return false;
        const [dx, , dz] = ground(sh2t(d.x), sh2t(d.y));
        return Math.hypot(dx - g.position.x, dz - g.position.z) < 0.8;
      });
      if (u3 && killed && this.isFoot(g) && motion?.die()) {
        // the soldier plays his 3D death animation; a bullet knocks him back, a blast throws him through the air
        this.units.delete(id);
        this.motions.delete(id);
        g.getObjectByName('ring')?.removeFromParent();
        const { vel, blast } = this.deathPush(g, now);
        if (vel.x * vel.x + vel.z * vel.z > 1e-4) g.rotation.y = Math.atan2(vel.z, -vel.x);
        this.dying.push({
          g, motion, at: now,
          vel: blast ? vel : vel.multiplyScalar(0.35).setY(0),
          axis: new THREE.Vector3(0, 1, 0), ang: 0, rate: 0, base: g.quaternion.clone(), landed: !blast,
        });
        continue;
      }
      this.dropUnit(id, g);
    }
    // the fallen are thrown, tumble and land, lie a moment, then sink into the ground
    const dyingMat = new THREE.Matrix4();
    this.dying = this.dying.filter(d => {
      d.motion?.update({ now, dt, speed: 0, turn: 0, aim: null, fired: false });
      const age = now - d.at;
      this.stepRagdoll(d, dt);
      if (age > CORPSE_MS) {
        const floor = this.bodies.groundAt(d.g.position.x, d.g.position.z);
        d.g.position.y = floor - ((age - CORPSE_MS) / SINK_MS) * 0.4;
      }
      const member = d.g.userData.crowd as CrowdMember | undefined;
      if (member) {
        dyingMat.compose(d.g.position, d.g.quaternion, d.g.scale);
        member.setMatrix(dyingMat, d.g.userData.scale as number);
      }
      if (age < CORPSE_MS + SINK_MS && u3) return true;
      d.motion?.dispose();
      this.unitGroup.remove(d.g);
      disposeTree(d.g);
      return false;
    });
  }

  /** Whether a unit's group is a man on foot, as opposed to a machine. */
  private isFoot(g: THREE.Group): boolean {
    return g.userData.kind === 'foot';
  }

  /**
   * How a man is thrown when he is killed, from the shot that killed him (found among the latest shots
   * landing where he stood): hard and high by an explosion, a backward stagger by a bullet.
   */
  private deathPush(g: THREE.Group, now: number): { vel: THREE.Vector3; blast: boolean } {
    let killer: LiveShot | null = null;
    let best = 0.9;
    for (const sh of shots3D()) {
      if (now - sh.at > 500) continue;
      const [tx, , tz] = ground(sh2t(sh.toX), sh2t(sh.toY));
      const d = Math.hypot(tx - g.position.x, tz - g.position.z);
      const blast = sh.by === 'strike' || EXPLOSIVE.has(sh.type);
      // a blast counts from further away than a bullet
      if (d < (blast ? 2.4 : best) && (!killer || blast || d < best)) { killer = sh; best = d; if (blast) break; }
    }
    const blast = !!killer && (killer.by === 'strike' || EXPLOSIVE.has(killer.type));
    const dir = new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5);
    if (killer) {
      const [fx, , fz] = ground(sh2t(killer.fromX), sh2t(killer.fromY));
      const [tx, , tz] = ground(sh2t(killer.toX), sh2t(killer.toY));
      // a blast throws him away from where it went off; a bullet knocks him away from the shooter
      const from = blast && (killer.by === 'strike' || killer.type === 'howitzer') ? new THREE.Vector3(tx, 0, tz) : new THREE.Vector3(fx, 0, fz);
      dir.set(g.position.x - from.x, 0, g.position.z - from.z);
      if (dir.lengthSq() < 1e-6) dir.set(Math.random() - 0.5, 0, Math.random() - 0.5);
    }
    dir.normalize();
    const speed = blast ? 2.5 + Math.random() * 3 : 0.8 + Math.random() * 0.8;
    return { vel: dir.multiplyScalar(speed).setY(blast ? 3 + Math.random() * 2.5 : 0.6), blast };
  }


  /**
   * A unit killed by a nuke, thrown away from ground zero: the nearer it stood, the harder and
   * higher, so the closest fly right across the map. A soldier goes tumbling as a ragdoll with his
   * death clip playing; a tank or any other machine goes as its whole hull, spinning end over end,
   * and blows apart into pieces where it comes down.
   */
  private fling(id: number, g: THREE.Group, death: DeathFx, now: number): void {
    const motion = this.motions.get(id);
    this.units.delete(id);
    this.motions.delete(id);
    g.getObjectByName('ring')?.removeFromParent();
    motion?.die();
    const [gx, , gz] = ground(sh2t(death.blast!.x), sh2t(death.blast!.y));
    const dir = new THREE.Vector3(g.position.x - gx, 0, g.position.z - gz);
    const d = dir.length();
    if (d < 1e-3) dir.set(Math.random() - 0.5, 0, Math.random() - 0.5);
    dir.normalize();
    const reach = ((DEFAULT_CONFIG.units.nuke?.strike?.radius ?? TILE * 5) / TILE) * 1.6;
    const close = Math.max(0.15, Math.min(1, 1 - d / reach));
    const style = unit3DStyle({ type: death.type ?? '' });
    const machine = style.kind !== 'foot';
    if (!machine) {
      // a soldier near ground zero plays his death clip as the shockwave throws him across the map
      const speed = (6 + 12 * close) * (0.85 + Math.random() * 0.3);
      const vel = dir.clone().multiplyScalar(speed).setY((3.5 + 5.5 * close) * (0.85 + Math.random() * 0.3));
      if (dir.x * dir.x + dir.z * dir.z > 1e-4) g.rotation.y = Math.atan2(dir.z, -dir.x);
      this.dying.push({
        g, motion, at: now, vel, axis: new THREE.Vector3(0, 1, 0), ang: 0, rate: 0, base: g.quaternion.clone(), landed: false,
      });
      return;
    }
    // a tank is heavier than a man, but nothing near ground zero stays on the ground
    const heavy = machine ? 0.72 : 1;
    const speed = (7 + 16 * close) * heavy * (0.85 + Math.random() * 0.3);
    const vel = dir.clone().multiplyScalar(speed).setY((5 + 8 * close) * heavy * (0.85 + Math.random() * 0.3));
    const axis = new THREE.Vector3(0, 1, 0).cross(dir).normalize();
    const rate = (6 + 10 * close) * (Math.random() < 0.8 ? 1 : -1) * (machine ? 0.7 : 1);
    this.dying.push({
      g, motion, at: now, vel, axis, ang: 0, rate, base: g.quaternion.clone(), landed: false,
      ...(machine ? { machine: { type: death.type ?? '', color: new THREE.Color(style.color), size: style.kind === 'ship' ? 1.7 : style.kind === 'vehicle' ? 1.25 : 1, burst: false } } : {}),
    });
  }

  /**
   * A blast at `pos`: everything loose within `radius` tiles is thrown again: building rubble,
   * gibs and wreckage, and the bodies of the fallen, which take off tumbling.
   */
  private kick(pos: THREE.Vector3, radius: number, power: number): void {
    this.debris.kick(pos, radius, power);
    this.fx.kick(pos, radius, power);
    this.bodies.kick(pos, radius, power);
    for (const d of this.dying) {
      const p = d.g.position;
      const dist = Math.hypot(p.x - pos.x, p.z - pos.z);
      if (dist > radius) continue;
      const f = power * (1 - dist / radius);
      const out = dist > 1e-3 ? new THREE.Vector3((p.x - pos.x) / dist, 0, (p.z - pos.z) / dist) : new THREE.Vector3(1, 0, 0);
      d.vel.addScaledVector(out, f * 0.7).add(new THREE.Vector3(0, f * 0.8, 0));
      if (d.machine) {
        d.axis.copy(new THREE.Vector3(0, 1, 0).cross(out).normalize());
        d.rate += (Math.random() < 0.5 ? -1 : 1) * f * 2.5;
      }
      d.landed = false;
    }
  }

  /** Moves a thrown body: ballistic flight and tumble, then a short slide and a settle flat on his back or front. */
  private stepRagdoll(d: Ragdoll, dt: number): void {
    const p = d.g.position;
    if (!d.landed) {
      p.addScaledVector(d.vel, dt);
      d.vel.y -= 9 * dt;
      d.ang += d.rate * dt;
      const floor = this.bodies.groundAt(p.x, p.z);
      if (p.y <= floor && d.vel.y < 0) {
        p.y = floor; d.landed = true; d.vel.y = 0;
        // a thrown machine comes down and blows apart where it lands
        if (d.machine && !d.machine.burst) {
          d.machine.burst = true;
          this.fx.wreck(p.clone(), d.machine.size, d.machine.color, d.machine.type === 'tank');
          this.kick(p.clone(), 1.2 + d.machine.size * 0.4, 3.5);
        }
      }
    } else {
      d.vel.multiplyScalar(Math.pow(0.01, dt));
      p.x += d.vel.x * dt;
      p.z += d.vel.z * dt;
      if (d.machine) {
        // settle to the nearest lying pose: flat on his back or on his face
        const flat = Math.round((d.ang - Math.PI / 2) / Math.PI) * Math.PI + Math.PI / 2;
        d.ang += (flat - d.ang) * Math.min(1, dt * 10);
      }
    }
    d.g.quaternion.setFromAxisAngle(d.axis, d.ang).multiply(d.base);
  }

  /** Where a unit's gun is: out in front of it along its aim, at the height it holds it; a tank or warship fires from its turret's barrel. */
  private unitMuzzle(g: THREE.Group, kind: string, aim: number): THREE.Vector3 {
    const [reach, up] = MUZZLE[kind] ?? [0.2, 0.25];
    const p = g.position.clone();
    const turret = kind === 'vehicle' || kind === 'ship' ? g.getObjectByName('turret') : undefined;
    if (turret) {
      turret.updateWorldMatrix(true, false);
      p.setFromMatrixPosition(turret.matrixWorld);
      p.y += kind === 'ship' ? 0.05 : 0.04;
      const r = kind === 'ship' ? 0.35 : reach * 0.8;
      p.x += Math.cos(aim) * r;
      p.z -= Math.sin(aim) * r;
      return p;
    }
    p.y += up;
    p.x += Math.cos(aim) * reach;
    p.z -= Math.sin(aim) * reach;
    return p;
  }

  /** The height a shot landing at this point should hit: the unit standing there, if there is one. */
  private targetHeight(x: number, z: number, fallback: number): number {
    let best = 0.6, h = fallback;
    for (const g of this.units.values()) {
      const d = Math.hypot(g.position.x - x, g.position.z - z);
      if (d < best) { best = d; h = g.position.y + 0.2; }
    }
    return h;
  }

  private dropUnit(id: number, g: THREE.Group): void {
    this.motions.get(id)?.dispose();
    this.motions.delete(id);
    this.unitGroup.remove(g);
    disposeTree(g);
    this.units.delete(id);
  }

  /** Sets off each new shot once: a flash at the gun, then a round, shell or missile that flies to its target and hits. */
  private syncShots(now: number): void {
    for (const sh of shots3D()) {
      if (this.seenShots.has(sh)) continue;
      this.seenShots.add(sh);
      // one that had already flown before this view started is left alone
      if (now - sh.at > 150) continue;
      const [tx, , tz] = ground(sh2t(sh.toX), sh2t(sh.toY));
      if (sh.by === 'strike') {
        const at = new THREE.Vector3(tx, 0, tz);
        // smoke and a repair are quiet: the puffs and the bar say it all
        if (sh.type === 'smoke' || sh.type === 'repair') continue;
        // the blast throws everything loose round it: rubble, gibs, wrecks and the dead
        if (sh.type === 'nuke') {
          // everything loose for a long way round is thrown, and buildings it brings down a moment later go the same way
          const r = (DEFAULT_CONFIG.units.nuke?.strike?.radius ?? TILE * 7) / TILE;
          this.kick(at, r * 1.6, 16);
          this.lastNuke = { at: at.clone(), when: now, r };
        }
        else if (sh.type === 'ballistic' || sh.type === 'hgv') this.kick(at, 2.8, 8);
        else if (sh.type === 'artillery') this.kick(at, 2.5, 6);
        else if (sh.type !== 'emp') this.kick(at, 1.5, 5);
        if (sh.type === 'nuke') this.fx.nuke(at, STRIKE_RADIUS.nuke);
        else if (sh.type === 'emp') this.fx.emp(at);
        // a ballistic missile or glide vehicle hitting: a big blast and a shake
        else if (sh.type === 'ballistic' || sh.type === 'hgv') { this.fx.blast(at, 2.4); this.fx.shakeBy(0.4); }
        else if (sh.type === 'artillery') { this.fx.blast(at, 1.9); this.fx.shakeBy(0.25); }
        else this.fx.blast(at, 1);
        continue;
      }
      // an interceptor that missed blows itself up where it is, up in the sky at the height it was coasting
      if (sh.type === 'selfdestruct') {
        const [ix, , iz] = ground(sh2t(sh.fromX), sh2t(sh.fromY));
        let iy = 1.5, best = 1.2;
        for (const g of this.units.values()) {
          if (g.userData.coastY === undefined) continue;
          const d = Math.hypot(g.position.x - ix, g.position.z - iz);
          if (d < best) { best = d; iy = g.position.y; }
        }
        this.fx.blast(new THREE.Vector3(ix, iy, iz), 0.45);
        continue;
      }
      // an interceptor reaching its target bursts in the air; one whose target is gone just pops
      if (sh.type === 'impact' || sh.type === 'fizzle') {
        const [ix, , iz] = ground(sh2t(sh.fromX), sh2t(sh.fromY));
        // it bursts where the missile met its target: up in the sky for a warhead or a missile on its arc
        let iy = sh.type === 'impact' ? 1.2 : 0.6, best = 0.8;
        for (const g of this.units.values()) {
          const d = Math.hypot(g.position.x - ix, g.position.z - iz);
          if (d < best) { best = d; iy = Math.max(0.3, g.position.y); }
        }
        this.fx.blast(new THREE.Vector3(ix, iy, iz), sh.type === 'impact' ? 0.6 : 0.3);
        continue;
      }
      const [fx, , fz] = ground(sh2t(sh.fromX), sh2t(sh.fromY));
      let from = this.muzzles.get(sh) ?? new THREE.Vector3(fx, sh.by === 'defence' ? 0.8 : 0.3, fz);
      if (!this.muzzles.has(sh) && sh.by === 'defence' && sh.type === 'bunker') {
        // bunker heavy machine guns fire from the stepped concrete embrasure facing the target
        const dx = tx - fx, dz = tz - fz, len = Math.hypot(dx, dz);
        if (len > 1e-4) from.set(fx + (dx / len) * 0.78, 0.42, fz + (dz / len) * 0.78);
      }
      // fired from something in the air (a jet, a helicopter, a drone): from where it is drawn, up there
      if (!this.muzzles.has(sh)) {
        let best = 0.8;
        for (const g of this.units.values()) {
          if (g.position.y < 0.5) continue;
          const d = Math.hypot(g.position.x - fx, g.position.z - fz);
          if (d < best) { best = d; from = g.position.clone(); }
        }
      }
      const to = new THREE.Vector3(tx, this.targetHeight(tx, tz, sh.by === 'unit' ? 0.35 : 0.25), tz);
      if (sh.type === 'jet_missile') {
        // a jet's air-to-ground missile: off the rail and down onto the target in half a second
        this.fx.missile(from, to, 450, now, false);
        continue;
      }
      // shells, rockets, bombs and mines going off on the ground throw the loose debris round them
      const k = KICKS[sh.type];
      if (k) this.kick(new THREE.Vector3(tx, 0, tz), k[0], k[1]);
      // a defence firing at something in the air aims at the thing itself, wherever it is drawn: a C-RAM's
      // rounds meet the missile, not the ground under it or its smoke trail
      if (sh.by === 'defence') {
        let best = 0.9;
        for (const g of this.units.values()) {
          const d = Math.hypot(g.position.x - tx, g.position.z - tz);
          if (d < best) { best = d; to.copy(g.position).setY(g.position.y + 0.05); }
        }
      }
      if (sh.type === 'catapult' || sh.type === 'launch') {
        // a jet shot off the carrier's deck or a ship launching a missile/drone offshore
        this.fx.launch(new THREE.Vector3(fx, 0.35, fz), sh.type === 'launch' ? 0.9 : 0.5);
        continue;
      }
      if (sh.type === 'fpv_drone' || sh.type === 'attack_drone' || sh.type === 'shahed') {
        // a one-way drone diving onto its target: it blows apart on impact
        const size = sh.type === 'shahed' ? 1.5 : sh.type === 'attack_drone' ? 1.15 : 0.95;
        this.fx.wreck(to, size, new THREE.Color(unit3DStyle({ type: sh.type }).color));
        continue;
      }
      if (sh.type === 'ballistic' || sh.type === 'hgv' || sh.type === 'icbm' || sh.type === 'hgv_nuclear' || sh.type === 'cruise_missile' || sh.type === 'kalibr' || sh.type === 'emp_missile') {
        const at = new THREE.Vector3(tx, 0, tz);
        if (sh.type === 'icbm' || sh.type === 'hgv_nuclear') {
          // a nuclear warhead going off: the blast as wide as the sim has it
          const r = (DEFAULT_CONFIG.units[sh.type]?.warhead?.radius ?? TILE * 7) / TILE;
          this.kick(at, r * 1.6, 16);
          this.lastNuke = { at: at.clone(), when: now, r };
          this.fx.nuke(at, r);
        } else if (sh.type === 'emp_missile') {
          this.fx.emp(at);
        } else if (sh.type === 'ballistic' || sh.type === 'hgv') {
          this.kick(at, 2.8, 8);
          this.fx.blast(at, 2.2);
          this.fx.shakeBy(0.35);
        } else {
          this.fx.blast(at, 1.5);
          this.fx.shakeBy(0.22);
        }
        continue;
      }
      if (sh.type === 'land_mine' || sh.type === 'at_mine' || sh.type === 'sea_mine') {
        this.fx.blast(new THREE.Vector3(tx, 0, tz), sh.type === 'at_mine' ? 1.35 : 1.0);
        continue;
      }
      if (sh.type === 'at_team') {
        this.fx.missile(from, to, 420, now, false);
        continue;
      }
      if (sh.type === 'flares') {
        // flares burst out behind the aircraft that dodged, wherever it is drawn
        const [ex, , ez] = ground(sh2t(sh.fromX), sh2t(sh.fromY));
        let at = new THREE.Vector3(ex, 1.5, ez), best = 1.5;
        for (const g of this.units.values()) {
          const d = Math.hypot(g.position.x - ex, g.position.z - ez);
          if (d < best && g.position.y > 0.5) { best = d; at = g.position.clone(); }
        }
        this.fx.flares(at);
        continue;
      }
      if (sh.type === 'missile_launch') {
        // a ballistic missile breaking the surface: a column of spray and steam, the motor lighting, the ground shaking
        this.fx.seaLaunch(from.clone().setY(0.05));
        continue;
      }
      if (MISSILE_LAUNCHERS.has(sh.type)) {
        // the missile itself is drawn from the sim, flying its arc; here it is just the launch blast
        this.fx.launch(from, 0.6);
        continue;
      }
      if (MISSILE_TYPES.has(sh.type)) {
        const air = !LOW_LAUNCHERS.has(sh.type);
        if (air) to.y = Math.max(to.y, 1);
        this.fx.missile(from, to, shotLife(sh.type), now, air);
        continue;
      }
      const calibre: Calibre = sh.by === 'defence' ? (TURRETS[sh.type]?.calibre ?? (sh.type === 'sniper_nest' ? 'sniper' : sh.type === 'bunker' ? 'small' : 'heavy')) : (UNIT_CALIBRE[sh.type] ?? 'small');
      const dir = to.clone().sub(from).setY(0);
      if (dir.lengthSq() > 1e-6) dir.normalize();
      this.fx.muzzle(from, dir, calibre);
      if (sh.miss) {
        // a round that missed carries on past its target and away, until it is out of sight
        const along = to.clone().sub(from);
        const len = along.length();
        if (len > 1e-3) to.addScaledVector(along.divideScalar(len), 40);
      }
      this.fx.bullet(from, to, calibre, now, !!sh.miss);
    }
  }

  /** Flat marks where units fell: red for people, a fading blast for machines. */
  private syncDeaths(now: number): void {
    const list = deaths3D();
    for (let i = 0; i < list.length; i++) {
      const d = list[i]!;
      let m = this.marks[i];
      if (!m) {
        m = new THREE.Mesh(new THREE.CircleGeometry(0.5, 16), new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false }));
        m.rotation.x = -Math.PI / 2;
        this.marks[i] = m;
        this.scene.add(m);
      }
      const age = (now - d.at) / DEATH_MS;
      if (!this.seenDeaths.has(d)) {
        this.seenDeaths.add(d);
        if (now - d.at < 300) {
          const [X, , Z] = ground(d.x / TILE, d.y / TILE);
          if (d.kind === 'flesh') this.fx.flesh(new THREE.Vector3(X, 0, Z), !!d.blast);
          else if (d.kind === 'debris') {
            // shot down: aircraft, drones and missiles blow up right in the air where they were hit
            const id = (d.seed - 7) / 31;
            const style = unit3DStyle({ type: d.type ?? '' });
            const at = this.lastInAir.get(id) ?? new THREE.Vector3(X, style.lift || 1.5, Z);
            this.lastInAir.delete(id);
            const size = style.kind === 'air' ? 1.45 : d.type === 'shahed' ? 1.2 : style.kind === 'drone' ? 0.95 : 1.05;
            this.fx.debris(at.clone(), size, new THREE.Color(style.color));
          } else if (!d.blast) {
            // the machine blows apart: pieces in its own colour, more and bigger for a ship, a tank's turret thrown high
            const style = unit3DStyle({ type: d.type ?? '' });
            const size = style.kind === 'ship' ? 1.7 : style.kind === 'vehicle' ? 1.25 : style.kind === 'air' ? 1.1 : 0.6;
            this.kick(new THREE.Vector3(X, 0, Z), 1.4 + size * 0.5, 4);
            this.fx.wreck(new THREE.Vector3(X, this.targetHeight(X, Z, 0.2) - 0.1, Z), size, new THREE.Color(style.color), d.type === 'tank');
          }
        }
      }
      // a scorch mark under a destroyed machine, but not under air debris or a fallen soldier
      m.visible = age >= 0 && age < 1 && d.kind !== 'debris' && d.kind !== 'flesh';
      if (!m.visible) continue;
      const mat = m.material as THREE.MeshBasicMaterial;
      const [X, , Z] = ground(d.x / TILE, d.y / TILE, 0.02);
      m.position.set(X, 0.02, Z);
      mat.color.setHex(WRECK);
      mat.opacity = 0.7 * Math.min(1, age * 6) * (1 - age);
      m.scale.setScalar(0.9 + Math.min(age, 0.15) * 3);
    }
    for (let i = list.length; i < this.marks.length; i++) this.marks[i]!.visible = false;
  }

  /** Lays a white sheet over the whole view while a nuke's flash lasts. */
  private syncWhiteout(now: number): void {
    const w = this.fx.whiteout(now);
    if (!this.whiteSheet && w > 0) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;inset:0;background:#fffaf0;pointer-events:none;z-index:5;opacity:0';
      document.body.appendChild(el);
      this.whiteSheet = el;
    }
    if (this.whiteSheet) this.whiteSheet.style.opacity = String(w);
  }

  /** The raid's smoke: a launcher's firing becomes a billowing launch cloud; missile trails are laid by the effects layer itself. */
  private syncSmoke(now: number): void {
    for (const p of smoke3D()) {
      if (this.seenSmoke.has(p)) continue;
      this.seenSmoke.add(p);
      if (now - p.at > 150 || p.at > now) continue;
      const [X, , Z] = ground(p.x / TILE, p.y / TILE);
      // the defender's smoke screen billows where its puffs are laid; a launch cloud flashes as well
      if (p.cloud) { this.fx.launch(new THREE.Vector3(X, 0.3, Z), 1.4); continue; }
      if (!p.flash) continue;
      this.fx.launch(new THREE.Vector3(X, 0.2, Z), Math.max(0.6, (p.size || 4) / 8));
    }
  }
}

/** Rough patches of olive, moss and dry earth, repeating: the countryside beyond the map. */
function countrysideTexture(): THREE.CanvasTexture {
  const n = 256;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  // distinct enough to read as varied ground once lit: moss, olive, scrub and dry earth
  const palette = ['#2f4a25', '#4a5a2c', '#3a3f26', '#6b5f3a', '#2a3a22', '#56633a', '#7a6a44'];
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  g.fillStyle = palette[0]!;
  g.fillRect(0, 0, n, n);
  for (let i = 0; i < 260; i++) {
    g.fillStyle = palette[Math.floor(rnd() * palette.length)]!;
    const x = rnd() * n, y = rnd() * n, r = 4 + rnd() * 18;
    g.beginPath();
    g.ellipse(x, y, r, r * (0.5 + rnd() * 0.5), rnd() * Math.PI, 0, Math.PI * 2);
    g.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * The pieces that make up one barrier tile. The model is a straight run one tile long along its
 * local x. Along each axis that joins a neighbour, a barrier takes a whole piece when it has
 * neighbours both ways, or half a piece from the tile centre towards the neighbour it joins, so
 * a corner or a T meets at the centre instead of poking past the other run. A lone barrier takes
 * a whole piece along the map's x.
 */
type BarrierPiece = { ax: number; ay: number; shift: number; length: number };
function barrierPieces(x: number, y: number, barriers: ReadonlySet<string>): BarrierPiece[] {
  const pieces: BarrierPiece[] = [];
  const axes: [number, number][] = [[1, 0], [0, 1]];
  for (const [ax, ay] of axes) {
    const pos = barriers.has(`${x + ax},${y + ay}`), neg = barriers.has(`${x - ax},${y - ay}`);
    if (!pos && !neg) continue;
    if (pos && neg) pieces.push({ ax, ay, shift: 0, length: 1 });
    else pieces.push({ ax, ay, shift: pos ? 0.25 : -0.25, length: 0.5 });
  }
  return pieces.length ? pieces : [{ ax: 1, ay: 0, shift: 0, length: 1 }];
}

/** The turn that lays a model's local x along the map step (ax, ay) on the ground. */
function barrierTurn(ax: number, ay: number): number {
  const [x0, , z0] = ground(0.5, 0.5);
  const [x1, , z1] = ground(0.5 + ax, 0.5 + ay);
  return Math.atan2(-(z1 - z0), x1 - x0);
}

/** A seeded random stream, so the ground looks the same every time. */
function seeded(seed: number): () => number {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) / 2147483647);
}
